/**
 * Flight Form Component
 * Form for creating and editing campaign flights
 * Supports email templates via SendGrid with merge field mapping
 */

import {
  ASSIGNED_AGENT_HELP,
  ASSIGNED_AGENT_VAR,
  ASSIGNED_CONCIERGE_HELP,
  ASSIGNED_CONCIERGE_VAR,
  checkFallbackReplyTo,
  checkReplyToValue,
  FROM_NAME_CONCIERGE_HELP,
  REPLY_TO_NAME_AGENT_HELP,
  REPLY_TO_NAME_CONCIERGE_HELP,
  REPLY_TO_NAME_HELP,
  SENDER_VARIABLE_TOKEN,
  SENDER_VARIABLE_WHO,
  senderVariableOf,
  SENDER_NAME_MAX,
  sanitizeSenderName,
} from "../../utils/senderVariable";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../../services/supabase";
import { fetchAllRows, pageByNameThenId } from "../../utils/fetchAllRows";
import { useTemplateGuardEnabled } from "../../hooks/useTemplateGuardEnabled";
import {
  getSMSTemplates,
  getCampaignOffers,
} from "../../services/campaigns.service";
import { getAttributeDefinitions } from "../../services/contactAttributes.service";
import {
  listActiveGroups,
  type SubscriptionGroup,
} from "../../services/subscriptionGroups.service";
import { getAvailableOffers } from "../../services/eventOffers.service";
import type { AttributeDefinition } from "../../types/contactAttribute.types";
import { isReminderSourceCandidate } from "../../utils/reminderSource";
import { initialMappingsForTemplate } from "../../utils/templateSwapMappings";
import {
  templateUsesInviteLink,
  willTokenizeInviteLink,
} from "../../utils/inviteLinkTokenization";

// Source-field prefix for contact custom attributes. Mirrors the
// form-handler autoresponder convention (FormHandlerEdit.tsx:45). The
// flight send path resolves "custom:<slug>" sourceFields against
// contact_attribute_values at render time (added 2026-05-16).
const CUSTOM_ATTRIBUTE_PREFIX = "custom:";
import ListAutocomplete from "../common/ListAutocomplete";
import CombineSegmentsModal from "./CombineSegmentsModal";
import TagAutocompleteInput from "../common/TagAutocompleteInput";
import TimezoneSelector, {
  DEFAULT_TIMEZONE,
  getTimezoneOffset,
} from "../common/TimezoneSelector";
import SendGridTemplateModal from "./SendGridTemplateModal";
import MergeFieldMapper from "./MergeFieldMapper";
import TemplatePreviewModal from "./TemplatePreviewModal";
import type {
  CampaignFlight,
  CreateFlightDTO,
  SMSTemplate,
} from "../../services/campaigns.service";
import type {
  FieldMapping,
  SendGridTemplateDetails,
} from "../../services/templates.service";
import {
  upsertTemplateRegistry,
  detectTemplateCapabilities,
  getSendGridTemplateDetails,
  substituteHandlebars,
  isSelfReferentialSubject,
  extractOverridableSubjectFallback,
  validateSubjectOverride,
  computeUnmappedTemplateVariables,
  detectForbiddenUnsubscribeTokens,
  forbiddenUnsubscribeMessage,
  detectRawSendgridUnsubscribeTags,
  rawSendgridUnsubscribeMessage,
  SYSTEM_FALLBACK_FIELDS,
  SENDGRID_AUTO_INJECTED_FIELDS,
  CONTACT_FIELDS,
} from "../../services/templates.service";
import {
  resolveContentUrlsForMappings,
  contentResolutionErrorLabel,
} from "../../services/content.service";
import {
  LANDING_LINK_CONTACT_VARS,
  previewLandingUrl,
  validateLandingConfig,
  normalizeCustomParams,
  type CustomParam,
  type LandingConfig,
} from "../../services/landingLink";
import {
  getZuoraEntitlementValues,
  ZUORA_FIELD_KIND,
  type ZuoraEntitlementValue,
} from "../../services/zuoraEntitlements.service";
import { STATIC_AUDIENCE_FILTER_FIELDS } from "./audienceFilterFields";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

// 'select' and 'number' added 2026-05-28 for custom-attribute filters.
// Select is text-stored but operator-restricted to atomic comparisons
// (no contains). Number is its own dispatch type with the same minimal
// surface as date (is_empty/is_not_empty/equals/not_equals).
type FilterFieldType =
  | "text"
  | "boolean"
  | "date"
  | "tag"
  | "select"
  | "number"
  | "list"
  | "email_quality"
  | "zuora";

const OPERATORS_BY_TYPE: Record<
  FilterFieldType,
  Array<{
    value: string;
    label: string;
    // 'scalar': uses condition.value (single string)
    // 'multi': uses condition.values (string[])
    // 'none':  no value input
    valueShape: "scalar" | "multi" | "none";
  }>
> = {
  text: [
    { value: "is_empty", label: "is empty", valueShape: "none" },
    { value: "is_not_empty", label: "is not empty", valueShape: "none" },
    { value: "equals", label: "equals", valueShape: "scalar" },
    { value: "not_equals", label: "does not equal", valueShape: "scalar" },
    { value: "contains", label: "contains", valueShape: "scalar" },
    { value: "not_contains", label: "does not contain", valueShape: "scalar" },
  ],
  // Select shares text storage but only atomic operators in v1 — substring
  // matches over a finite vocabulary mislead operators.
  select: [
    { value: "is_empty", label: "is empty", valueShape: "none" },
    { value: "is_not_empty", label: "is not empty", valueShape: "none" },
    { value: "equals", label: "equals", valueShape: "scalar" },
    { value: "not_equals", label: "does not equal", valueShape: "scalar" },
  ],
  boolean: [
    { value: "is_true", label: "is true", valueShape: "none" },
    { value: "is_false", label: "is false or empty", valueShape: "none" },
  ],
  date: [
    { value: "is_empty", label: "is empty", valueShape: "none" },
    { value: "is_not_empty", label: "is not empty", valueShape: "none" },
    { value: "equals", label: "is on", valueShape: "scalar" },
    { value: "not_equals", label: "is not on", valueShape: "scalar" },
    { value: "greater_than", label: "is after", valueShape: "scalar" },
    { value: "less_than", label: "is before", valueShape: "scalar" },
    { value: "days_ago", label: "within last N days", valueShape: "scalar" },
  ],
  number: [
    { value: "is_empty", label: "is empty", valueShape: "none" },
    { value: "is_not_empty", label: "is not empty", valueShape: "none" },
    { value: "equals", label: "equals", valueShape: "scalar" },
    { value: "not_equals", label: "does not equal", valueShape: "scalar" },
  ],
  tag: [
    { value: "has_any_of", label: "has any of", valueShape: "multi" },
    { value: "has_all_of", label: "has all of", valueShape: "multi" },
    { value: "does_not_have", label: "does not have", valueShape: "scalar" },
    { value: "has_none_of", label: "has none of", valueShape: "multi" },
  ],
  // List membership — value (scalar) holds a list_id, rendered as a picker.
  list: [
    { value: "in_list", label: "is on list", valueShape: "scalar" },
    { value: "not_in_list", label: "is not on list", valueShape: "scalar" },
  ],
  // NeverBounce email quality — value (scalar) is one of the 4 buckets,
  // rendered as a dropdown. Only equals/not_equals (the value is always one of
  // the 4 buckets, so is_empty/is_not_empty are meaningless). 'invalid' is
  // already auto-excluded from every send by the always-on guard.
  email_quality: [
    { value: "equals", label: "is", valueShape: "scalar" },
    { value: "not_equals", label: "is not", valueShape: "scalar" },
  ],
  // Zuora product / plan — matched against the contact's CURRENT ACTIVE subscriptions only.
  // Labels say "current" out loud because a cancelled member keeping their old plan name is
  // exactly the surprise operators would otherwise hit.
  zuora: [
    { value: "has_any_of", label: "is any of (current)", valueShape: "multi" },
    { value: "has_all_of", label: "has all of (current)", valueShape: "multi" },
    {
      value: "has_none_of",
      label: "is none of (current)",
      valueShape: "multi",
    },
    {
      value: "is_not_empty",
      label: "has any current plan",
      valueShape: "none",
    },
    { value: "is_empty", label: "has no current plan", valueShape: "none" },
  ],
};

// The 4 exposed NeverBounce buckets for the audience-filter dropdown. Labels
// carry the explanation inline (matches the segment builder's options).
const EMAIL_QUALITY_FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: "valid", label: "Valid — confirmed deliverable" },
  { value: "risky", label: "Risky — unconfirmed (catch-all / no response)" },
  { value: "invalid", label: "Invalid — undeliverable (auto-excluded)" },
  { value: "unvalidated", label: "Not yet validated" },
];

/** Map contact_attribute_definitions.data_type → audience-filter FilterFieldType. */
function customAttrDataTypeToFilterType(dt: string): FilterFieldType {
  switch (dt) {
    case "boolean":
      return "boolean";
    case "date":
      return "date";
    case "number":
      return "number";
    case "select":
      return "select";
    default:
      return "text";
  }
}

interface AudienceCondition {
  field: string;
  operator: string;
  value: string; // scalar input
  values?: string[]; // multi-value input (tag operators)
}

/**
 * Multi-tag pill input used by the audience filter when the operator's
 * valueShape is 'multi' (has_any_of / has_all_of / has_none_of). Renders
 * each selected tag as a removable pill plus a small picker that lists
 * suggestions from `availableTags` minus already-selected ones, with
 * free-text fallback so operators can type a tag the picker doesn't
 * surface.
 */
const TagPillInput: React.FC<{
  values: string[];
  suggestions: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}> = ({ values, disabled, onChange }) => {
  const [draft, setDraft] = useState("");
  const commit = (raw: string) => {
    const clean = raw.trim();
    if (!clean || values.includes(clean)) {
      setDraft("");
      return;
    }
    onChange([...values, clean]);
    setDraft("");
  };
  return (
    <div className="flex-1 min-w-[180px] flex flex-wrap items-center gap-1 px-2 py-1 border border-gray-300 rounded text-sm focus-within:ring-2 focus-within:ring-primary-500">
      {values.map((v) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-xs"
        >
          {v}
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="text-primary-500 hover:text-primary-700"
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          type="text"
          list="audience-filter-tag-suggestions"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit(draft);
            } else if (
              e.key === "Backspace" &&
              draft === "" &&
              values.length > 0
            ) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => commit(draft)}
          placeholder={values.length === 0 ? "add tag…" : ""}
          className="flex-1 min-w-[80px] outline-none text-sm bg-transparent"
        />
      )}
      {/* Suggestions render at the audience-filter section level, shared
          across every tag input (including the does_not_have scalar
          input). Avoids duplicate DOM ids if multiple TagPillInputs are
          rendered for sibling conditions. */}
    </div>
  );
};

// Retarget exclusion conditions
const RETARGET_EXCLUDE_OPTIONS = [
  { value: "voucher_not_redeemed", label: "Did NOT redeem voucher" },
  { value: "email_not_opened", label: "Did NOT open email" },
  { value: "email_not_clicked", label: "Did NOT click email" },
];

// Per-flight sender identity options. MIRRORS the server-side allow-list in
// _shared/flightSendHelpers.ts (resolveSender) — the server is authoritative and
// re-validates, so this is only the picklist. Default is first.
const DEFAULT_FROM_EMAIL = "noreply@uslawshield.com";
const SENDER_OPTIONS: { email: string; label: string }[] = [
  {
    email: "noreply@uslawshield.com",
    label: "U.S. LawShield (noreply@uslawshield.com)",
  },
  {
    email: "noreply@2ainstitute.com",
    label: "2A Institute (noreply@2ainstitute.com)",
  },
];

// {{assigned_agent}} (mig 1550): a third "Send from" choice, resolved per recipient by the send path. Not an identity
// itself: when chosen, a Fallback sender (one of SENDER_OPTIONS) is required.
const AGENT_FROM_OPTION = {
  email: ASSIGNED_AGENT_VAR,
  label: `Recipient's support agent (${ASSIGNED_AGENT_VAR})`,
};
// {{assigned_concierge}} (mig 1770): the concierge of the recipient's organization; same fallback rules.
const CONCIERGE_FROM_OPTION = {
  email: ASSIGNED_CONCIERGE_VAR,
  label: `Recipient's organization concierge (${ASSIGNED_CONCIERGE_VAR})`,
};

// Stable empty-array reference for the siblingFlights default. A fresh `[]`
// default would be a new reference every render, and siblingFlights is a
// dependency of the invite-link resolver effect — a caller that omits the prop
// would otherwise trigger a Supabase re-query on every render (audit NIT).
const EMPTY_SIBLING_FLIGHTS: CampaignFlight[] = [];

interface Props {
  campaignId: string;
  flight?: CampaignFlight | null;
  siblingFlights?: CampaignFlight[];
  onSubmit: (data: CreateFlightDTO) => void;
  onCancel: () => void;
  // Optional: called when the operator clicks "Cancel Schedule" from
  // inside the edit-flight form. The parent (FlightDetail) owns the
  // actual cancel flow (confirmation modal + updateFlight + reload);
  // FlightForm only surfaces the button as an in-form affordance.
  onRequestCancelSchedule?: () => void;
  loading?: boolean;
  // The campaign is tied to an event. A NEW flight then defaults its Email subscription
  // category to "Events" (2026-09-30), so a mail client's Unsubscribe button removes the
  // recipient from event mail only, not from everything. The operator can still change it.
  isEventCampaign?: boolean;
}

const FlightForm: React.FC<Props> = ({
  campaignId,
  flight,
  siblingFlights = EMPTY_SIBLING_FLIGHTS,
  onSubmit,
  onCancel,
  onRequestCancelSchedule,
  loading = false,
  isEventCampaign = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    list_id: "",
    template_id: "",
    sms_template_id: "",
    email_template_id: "",
    email_subject: "",
    scheduled_send_at: "",
    offer_id: "",
    subscription_group_id: "",
    sendgrid_ip_pool_key: "default",
  });
  // Tracks the saved list_id when it doesn't resolve to a current
  // segment (deleted post-save). Reported by ListAutocomplete via
  // onStaleValue. While set, save/schedule is blocked — the user must
  // pick a current segment to clear it.
  const [staleListId, setStaleListId] = useState<string | null>(null);

  const [smsTemplates, setSmsTemplates] = useState<SMSTemplate[]>([]);
  const [campaignOffers, setCampaignOffers] = useState<
    Array<{
      id: string;
      offer_id: string;
      priority: number;
      offer: {
        id: string;
        name: string;
        offer_type: string;
        value_amount: number | null;
        is_active: boolean;
      };
    }>
  >([]);
  // All active offers — used to populate the Associated Offer selector when
  // generating vouchers (the voucher derives from any chosen offer, not only
  // offers pre-attached to the campaign).
  const [availableOffers, setAvailableOffers] = useState<
    Array<{ id: string; name: string; value_amount?: number }>
  >([]);
  const [templateSupportsVoucher, setTemplateSupportsVoucher] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [subscriptionGroups, setSubscriptionGroups] = useState<
    SubscriptionGroup[]
  >([]);
  // True once the Events default has been applied (or the operator picked a category), so the
  // default is applied at most once and never overrides a choice.
  const eventsGroupDefaultedRef = useRef(false);
  const [eventsGroupDefaulted, setEventsGroupDefaulted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // SendGrid template state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] =
    useState<SendGridTemplateDetails | null>(null);
  // ASM template-guard flag (FG-B1): gates the dead-unsubscribe-link block below. OFF (default)
  // during deploy-dark → warn only; ON → hard block.
  const templateGuardEnabled = useTemplateGuardEnabled();
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);

  // Save-time validation of sourceType='content' mappings (audit-AI
  // 2026-06-04 R3 must-fix). Loaded async whenever fieldMappings
  // change; the sync validate() reads from this state. Each entry
  // identifies a broken content reference + its reason ('missing' /
  // 'not_public' / 'not_image' / 'no_storage_url'). When non-empty,
  // save is blocked with an error banner.
  const [contentMappingErrors, setContentMappingErrors] = useState<
    Array<{ contentId: string; reason: string; label: string }>
  >([]);

  // Active contact-attribute definitions surfaced in the mapper dropdown
  // alongside the standard CONTACT_FIELDS list. Each becomes an
  // { value: 'custom:<slug>', label: 'Attribute: <name>' } entry the
  // operator can pick as a contact source for a template merge variable.
  // The flight send path resolves these against contact_attribute_values
  // at render time. Definitions are filtered to is_active=TRUE; the
  // operator picks what makes sense for their audience.
  const [activeAttributes, setActiveAttributes] = useState<
    AttributeDefinition[]
  >([]);

  // Toggle for voucher generation
  const [generateVouchers, setGenerateVouchers] = useState(false);

  // Reminder flights (Fix E): reuse an earlier voucher flight's codes + list.
  // When on, this flight generates nothing; it shows each recipient the source
  // flight's voucher and inherits the source's list (no separate list picked).
  const [isReminder, setIsReminder] = useState(false);
  const [reminderSourceFlightId, setReminderSourceFlightId] = useState("");
  // #4: whether a reminder also sends to members added to the segment since the original send.
  // OFF (default) = original recipients only.
  const [reminderIncludeNewMembers, setReminderIncludeNewMembers] =
    useState(false);

  // Channel selection: email or sms (single-channel flights)
  const [channel, setChannel] = useState<"email" | "sms">("email");

  // Multi-segment combine (A1): opens CombineSegmentsModal to build a static snapshot list from
  // several segments, then targets this flight at the resulting combined list.
  const [showCombineModal, setShowCombineModal] = useState(false);

  // Retarget configuration
  const [audienceMode, setAudienceMode] = useState<"full" | "retarget">("full");
  const [retargetFlightId, setRetargetFlightId] = useState("");
  const [retargetExcludeEvent, setRetargetExcludeEvent] = useState(
    "voucher_not_redeemed",
  );

  // Schedule timezone
  const [sendTimezone, setSendTimezone] = useState(DEFAULT_TIMEZONE);

  // Tag actions
  const [applyTag, setApplyTag] = useState("");
  const [removeTag, setRemoveTag] = useState("");

  // Phase 2 — per-flight overrides stored in flight.metadata.
  // - preheader  → injected as templateData.preheader on send; operator's
  //                template needs `{{preheader}}` in a hidden span to show
  //                in inboxes.
  // - fromName   → SendGrid `from.name` override (display name only;
  //                from.email stays noreply@uslawshield.com for SPF/DKIM).
  // - replyTo    → SendGrid send-time `reply_to` override. Empty by default,
  //                so replies route to the from address (noreply@uslawshield.com,
  //                the standard U.S. LawShield reply target). Set per flight to
  //                redirect replies elsewhere (e.g. a partner's inbox). Stored
  //                as metadata.reply_to = { email } and threaded into sendOne.
  const [preheader, setPreheader] = useState("");
  const [fromName, setFromName] = useState("");
  // Per-flight sender email (from-domain). Default = uslawshield.com; operators
  // can also pick the 2ainstitute.com identity. This changes only the
  // allow-listed From address, NOT the outbound IP/pool. Stored as
  // metadata.from_email.
  const [fromEmail, setFromEmail] = useState(DEFAULT_FROM_EMAIL);
  const [replyTo, setReplyTo] = useState("");
  // Reply-To display name (owner 2026-11-04): metadata.reply_to_name. Applies to a fixed Reply-To and to the fallback;
  // an agent Reply-To is always shown under the agent's own name.
  const [replyToName, setReplyToName] = useState("");
  // {{assigned_agent}} fallbacks (mig 1550): used for a recipient whose agent can't be used.
  const [fromFallbackEmail, setFromFallbackEmail] =
    useState(DEFAULT_FROM_EMAIL);
  const [replyToFallback, setReplyToFallback] = useState("");
  // A per-recipient sender variable: {{assigned_agent}} (mig 1550) or {{assigned_concierge}} (mig 1770). The
  // "...IsAgent" names are kept from 1550 and mean "uses a sender variable" (both share the fallback fields).
  const fromVariable = senderVariableOf(fromEmail);
  const replyToVariable = senderVariableOf(replyTo);
  const fromIsAgent = fromVariable !== null;
  const replyToIsAgent = replyToVariable !== null;
  const fromWho = fromVariable ? SENDER_VARIABLE_WHO[fromVariable] : "";
  const replyWho = replyToVariable ? SENDER_VARIABLE_WHO[replyToVariable] : "";
  // The fixed identity a send without an agent uses (the fallback in agent mode).
  const effectiveFixedFrom = fromIsAgent ? fromFallbackEmail : fromEmail;

  // Landing page link config
  // landingPageEnabled is derived from whether any field mapping has source
  // type 'landing_page'. Operators flip the toggle by changing the source
  // dropdown in the mapper for the variable they want the URL injected
  // into (typically {{landing_page_link}}). Backward compat for old flights:
  // the load path synthesises a landing_page mapping when
  // metadata.landing_page_config exists without a corresponding mapping.
  const landingPageEnabled = useMemo(
    () => fieldMappings.some((m) => m.sourceType === "landing_page"),
    [fieldMappings],
  );

  // Background validation for sourceType='content' mappings. Runs
  // whenever the mapping set changes; updates `contentMappingErrors`
  // which the sync validate() reads at save time.
  const contentMappingIds = useMemo(
    () =>
      fieldMappings
        .filter((m) => m.sourceType === "content" && m.sourceField)
        .map((m) => m.sourceField as string),
    [fieldMappings],
  );
  useEffect(() => {
    if (contentMappingIds.length === 0) {
      setContentMappingErrors([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await resolveContentUrlsForMappings(contentMappingIds);
        if (cancelled) {
          return;
        }
        setContentMappingErrors(
          res.errors.map((e) => ({
            contentId: e.contentId,
            reason: e.reason,
            label: contentResolutionErrorLabel(e.reason),
          })),
        );
      } catch {
        if (cancelled) {
          return;
        }
        setContentMappingErrors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contentMappingIds]);
  const [landingPageConfig, setLandingPageConfig] = useState({
    base_url: "",
    utm_id: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
    affid: "",
    promo: "",
    ste: "",
  });
  // Append each recipient's email/first/last/phone to the landing-page link as
  // registration prefill params (kept separate from the string-map config).
  const [landingPagePrefill, setLandingPagePrefill] = useState(false);
  // Operator-defined custom query parameters (name/value rows). Kept as a
  // separate array (the fixed config above is a string map); merged into
  // landing_page_config.custom_params on save. Values may contain {{vars}}.
  const [customParams, setCustomParams] = useState<CustomParam[]>([]);
  const [templateHasLandingPageLink, setTemplateHasLandingPageLink] =
    useState(false);
  // EIB silent-blast guard: does the selected template use {{invite_link}}?
  // The send path only injects a personalized (tokenized) invite_link when the
  // flight's offer is a $0 placeholder bound to an invite_gated event
  // (send-campaign-flight/index.ts ~L205-231). Any other context renders
  // {{invite_link}} EMPTY to the whole audience — a tokenless blast that
  // reports all-sent. We mirror that exact resolution here and block scheduling.
  const [templateHasInviteLink, setTemplateHasInviteLink] = useState(false);
  // Resolved once we know the flight's effective offer: whether the send path
  // WILL tokenize invite_link. `loaded` distinguishes "not resolved yet"
  // (don't false-block) from a confirmed non-tokenizing context.
  const [inviteContext, setInviteContext] = useState<{
    loaded: boolean;
    willTokenize: boolean;
  }>({ loaded: false, willTokenize: false });
  const [showVariableHints, setShowVariableHints] = useState(false);

  // Per-flight risky opt-out. Default OFF = risky (unverified) addresses are INCLUDED,
  // exactly as today. When ON, this flight ALSO excludes verdict='risky' at send (invalid
  // is always excluded regardless). Persisted as metadata.exclude_risky.
  const [excludeRisky, setExcludeRisky] = useState(false);

  // Audience filter state
  const [audienceFiltersEnabled, setAudienceFiltersEnabled] = useState(false);
  const [audienceFiltersMatch, setAudienceFiltersMatch] = useState<
    "all" | "any"
  >("all");
  const [audienceFilterConditions, setAudienceFilterConditions] = useState<
    AudienceCondition[]
  >([]);

  // Selectable Zuora values, loaded once per builder from the same active-subscription source the
  // filters evaluate against, so an operator cannot pick a value that can never match.
  const [zuoraValues, setZuoraValues] = useState<
    Record<"product" | "plan", ZuoraEntitlementValue[]>
  >({ product: [], plan: [] });
  const [zuoraValuesError, setZuoraValuesError] = useState<string | null>(null);
  // Per-condition filter text, keyed by condition index. 355 values is too many to scroll.
  const [zuoraFilters, setZuoraFilters] = useState<Record<number, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [product, plan] = await Promise.all([
          getZuoraEntitlementValues("product"),
          getZuoraEntitlementValues("plan"),
        ]);
        if (!cancelled) {
          setZuoraValues({ product, plan });
          setZuoraValuesError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setZuoraValuesError(
            err instanceof Error ? err.message : "Could not load Zuora values",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // Distinct tag list for the audience-filter tag picker. Sourced from
  // contact_tags (the canonical place tags live) and deduplicated client-side.
  // Populated lazily — only fetched if the operator actually adds a tag
  // condition, so most flights never pay the cost.
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagsLoaded, setTagsLoaded] = useState(false);

  const ensureTagsLoaded = useCallback(async () => {
    if (tagsLoaded) {
      return;
    }
    try {
      const { data, error } = await supabase
        .from("contact_tags")
        .select("tag")
        .order("tag")
        .limit(2000);
      if (error) {
        console.error("Failed to load contact_tags for filter:", error);
        return;
      }
      const seen = new Set<string>();
      for (const row of (data || []) as Array<{ tag: string }>) {
        if (typeof row.tag === "string" && row.tag.trim()) {
          seen.add(row.tag.trim());
        }
      }
      setAvailableTags(Array.from(seen).sort((a, b) => a.localeCompare(b)));
      setTagsLoaded(true);
    } catch (err) {
      console.error("Failed to load contact_tags for filter:", err);
    }
  }, [tagsLoaded]);

  // Lists for the list-membership filter picker. Lazily loaded — only when a
  // list_membership condition exists. STATIC lists only (membership resolves via
  // list_contacts; dynamic segments have no rows there). The server also
  // enforces static-only and fails closed, so this is UX, not the guarantee.
  const [availableLists, setAvailableLists] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [listsLoaded, setListsLoaded] = useState(false);

  const ensureListsLoaded = useCallback(async () => {
    if (listsLoaded) {
      return;
    }
    try {
      // Membership is resolved via list_contacts (static rows). Dynamic
      // segments have no rows there, so restrict the picker to static lists
      // to avoid a silently-wrong audience. Paged: `.limit(2000)` does NOT
      // lift PostgREST's 1000-row cap, so later names were silently missing.
      let data: Array<{ id: string; name: string }>;
      try {
        data = await fetchAllRows<{ id: string; name: string }>((from, to) =>
          pageByNameThenId(
            supabase
              .from("lists")
              .select("id, name, is_dynamic")
              .eq("is_dynamic", false)
              .is("archived_at", null),
            from,
            to,
          ),
        );
      } catch (error) {
        console.error("Failed to load lists for filter:", error);
        return;
      }
      setAvailableLists(
        ((data || []) as Array<{ id: string; name: string }>).filter(
          (l) => l.id && l.name,
        ),
      );
      setListsLoaded(true);
    } catch (err) {
      console.error("Failed to load lists for filter:", err);
    }
  }, [listsLoaded]);

  // Load lists whenever a list-membership condition is present (e.g. editing a
  // saved flight) so the picker can show the selected list's name.
  useEffect(() => {
    if (audienceFilterConditions.some((c) => c.field === "list_membership")) {
      void ensureListsLoaded();
    }
  }, [audienceFilterConditions, ensureListsLoaded]);

  // Load templates and content
  useEffect(() => {
    loadData();
  }, []);

  // Load flight data if editing
  useEffect(() => {
    if (flight) {
      // Parse scheduled datetime for the local input. CRITICAL: render the
      // wall-clock time in flight.metadata.send_timezone, NOT the browser's
      // local zone. A California operator editing a flight scheduled for
      // 9 AM Central must see "09:00" in the input, not "07:00" — and on
      // resave the value gets re-encoded with the same send_timezone, so
      // the round trip is correct. Falling back to browser-local only when
      // no send_timezone is configured.
      let scheduledLocal = "";
      if (flight.scheduled_send_at) {
        const d = new Date(flight.scheduled_send_at);
        const tz =
          (flight.metadata?.send_timezone as string | undefined) ?? undefined;
        if (tz) {
          // Use Intl.DateTimeFormat parts so we get YYYY-MM-DDTHH:MM
          // rendered in the target timezone reliably across browsers.
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).formatToParts(d);
          const get = (type: string) =>
            parts.find((p) => p.type === type)?.value ?? "00";
          scheduledLocal = `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
        } else {
          // No timezone metadata — fall back to browser-local rendering
          // (matches pre-Phase-2 behavior so old flights load identically).
          scheduledLocal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        }
      }
      setFormData({
        name: flight.name,
        description: flight.description || "",
        list_id: flight.list_id || "",
        template_id: flight.template_id || "",
        sms_template_id: flight.sms_template_id || "",
        email_template_id: flight.email_template_id || "",
        email_subject: flight.email_subject || "",
        scheduled_send_at: scheduledLocal,
        offer_id: (flight as any).offer_id || "",
        subscription_group_id: (flight as any).subscription_group_id || "",
        // Existing flights have no stored key until saved; the column default is "default".
        sendgrid_ip_pool_key: (flight as any).sendgrid_ip_pool_key || "default",
      });
      // Detect channel from existing data
      if (flight.sms_template_id && !flight.email_template_id) {
        setChannel("sms");
      } else {
        setChannel("email");
      }
      // Restore timezone if stored in metadata
      if (flight.metadata?.send_timezone) {
        setSendTimezone(flight.metadata.send_timezone as string);
      }
      // Restore retarget config if stored in metadata
      if (flight.metadata?.retarget_flight_id) {
        setAudienceMode("retarget");
        setRetargetFlightId(flight.metadata.retarget_flight_id as string);
        setRetargetExcludeEvent(
          (flight.metadata.retarget_exclude_event as string) ||
            "voucher_not_redeemed",
        );
      }
      setGenerateVouchers(
        !!(flight as { generate_vouchers?: boolean }).generate_vouchers,
      );

      // Reminder flight (Fix E): restore the source link. Always set BOTH (audit fix)
      // so a reused form instance can't retain stale reminder state from a prior edit.
      const srcFlightId = (
        flight as { voucher_source_flight_id?: string | null }
      ).voucher_source_flight_id;
      setIsReminder(Boolean(srcFlightId));
      setReminderSourceFlightId(srcFlightId ?? "");
      setReminderIncludeNewMembers(
        (flight.metadata as Record<string, unknown> | null)?.[
          "reminder_include_new_members"
        ] === true,
      );

      // Restore tag actions from metadata
      if (flight.metadata?.apply_tag) {
        setApplyTag(flight.metadata.apply_tag as string);
      }
      if (flight.metadata?.remove_tag) {
        setRemoveTag(flight.metadata.remove_tag as string);
      }

      // Phase 2: restore preheader + from-name overrides from metadata.
      if (flight.metadata?.preheader) {
        setPreheader(flight.metadata.preheader as string);
      }
      if (flight.metadata?.from_name) {
        setFromName(flight.metadata.from_name as string);
      }
      if (typeof flight.metadata?.from_email === "string") {
        const stored = (flight.metadata.from_email as string).toLowerCase();
        // Only restore a known option; anything else falls back to the default
        // (the server allow-list would reject it anyway).
        setFromEmail(
          senderVariableOf(stored)
            ? SENDER_VARIABLE_TOKEN[senderVariableOf(stored)!]
            : SENDER_OPTIONS.some((o) => o.email === stored)
              ? stored
              : DEFAULT_FROM_EMAIL,
        );
      }
      if (typeof flight.metadata?.from_fallback_email === "string") {
        const fb = (
          flight.metadata.from_fallback_email as string
        ).toLowerCase();
        setFromFallbackEmail(
          SENDER_OPTIONS.some((o) => o.email === fb) ? fb : DEFAULT_FROM_EMAIL,
        );
      }
      {
        const rtf = flight.metadata?.reply_to_fallback as
          | { email?: string }
          | string
          | undefined;
        const rtfEmail = typeof rtf === "string" ? rtf : rtf?.email;
        if (rtfEmail) {
          setReplyToFallback(rtfEmail);
        }
      }
      // Reply-To override is stored as an object { email, name? }; the form
      // edits the email. Tolerate a legacy bare-string value too.
      if (flight.metadata?.reply_to) {
        const rt = flight.metadata.reply_to as
          | { email?: string; name?: string }
          | string
          | undefined;
        const rtEmail = typeof rt === "string" ? rt : rt?.email;
        if (rtEmail) {
          setReplyTo(rtEmail);
        }
        // A name inside the object (legacy SQL rows only) is kept by moving it to reply_to_name on save.
        if (
          typeof flight.metadata?.reply_to_name !== "string" &&
          rt &&
          typeof rt === "object" &&
          typeof rt.name === "string"
        ) {
          setReplyToName(rt.name);
        }
      }
      if (typeof flight.metadata?.reply_to_name === "string") {
        setReplyToName(flight.metadata.reply_to_name as string);
      }

      // Load field mappings if present
      const loadedMappings: FieldMapping[] =
        (flight.email_template_data as any)?.field_mappings ?? [];

      // Backward compat for flights that pre-date the unified
      // landing_page mapper: if the flight has metadata.landing_page_config
      // (the old shape) but no mapping with sourceType='landing_page',
      // synthesise one for {{landing_page_link}} so the URL builder panel
      // shows in the new UI.
      const lpCfgEnabled =
        flight.metadata?.landing_page_config &&
        (flight.metadata.landing_page_config as Record<string, unknown>)
          .enabled === true;
      const alreadyHasLpMapping = loadedMappings.some(
        (m) => m.sourceType === "landing_page",
      );
      if (lpCfgEnabled && !alreadyHasLpMapping) {
        loadedMappings.push({
          templateField: "landing_page_link",
          sourceType: "landing_page",
        });
      }

      if (loadedMappings.length > 0) {
        setFieldMappings(loadedMappings);
      }

      // Restore per-flight risky opt-out from metadata (absent = OFF).
      setExcludeRisky(
        (flight.metadata as Record<string, unknown> | null)?.exclude_risky ===
          true,
      );

      // Restore audience filters from metadata
      if (flight.metadata?.audience_filters) {
        const af = flight.metadata.audience_filters as {
          enabled?: boolean;
          match?: "all" | "any";
          conditions?: AudienceCondition[];
        };
        setAudienceFiltersEnabled(!!af.enabled);
        setAudienceFiltersMatch(af.match ?? "all");
        const restored = af.conditions ?? [];
        setAudienceFilterConditions(restored);
        // If any restored condition uses the tag field, kick off the
        // tag-list fetch so the picker shows suggestions when the
        // section is expanded.
        if (restored.some((c) => c.field === "tags")) {
          void ensureTagsLoaded();
        }
      }

      // Restore landing page config from metadata. Whether the panel is
      // shown is now driven by landingPageEnabled (memoised from the
      // mapper above), so we only need to hydrate the config values here.
      if (flight.metadata?.landing_page_config) {
        const lp = flight.metadata.landing_page_config as Record<
          string,
          string | boolean
        >;
        setLandingPageConfig({
          base_url: (lp.base_url as string) || "",
          utm_id: (lp.utm_id as string) || "",
          utm_source: (lp.utm_source as string) || "",
          utm_medium: (lp.utm_medium as string) || "",
          utm_campaign: (lp.utm_campaign as string) || "",
          utm_term: (lp.utm_term as string) || "",
          utm_content: (lp.utm_content as string) || "",
          affid: (lp.affid as string) || "",
          promo: (lp.promo as string) || "",
          ste: (lp.ste as string) || "",
        });
        setLandingPagePrefill(Boolean(lp.prefill_registration));
        // Rehydrate custom params in saved order. Legacy flights have no
        // custom_params key → empty array (backward compatible).
        const savedCustom = (
          flight.metadata.landing_page_config as Record<string, unknown>
        ).custom_params;
        setCustomParams(
          Array.isArray(savedCustom)
            ? (savedCustom as CustomParam[]).map((r) => ({
                name: String(r?.name ?? ""),
                value: String(r?.value ?? ""),
                ...(r?.required ? { required: true } : {}),
              }))
            : [],
        );
      }
    }
  }, [flight]);

  // Edit mode: hydrate `selectedEmailTemplate` from the saved
  // email_template_id so the template card renders the saved choice
  // instead of the empty "select a template" state. CRITICAL: do NOT
  // call handleTemplateSelect — that path resets field_mappings to
  // defaults and would wipe the operator's existing per-variable
  // configuration. We only set the display state + capability flags
  // here; the field_mappings hydration happens above from
  // flight.email_template_data.
  useEffect(() => {
    if (!flight?.email_template_id) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const details = await getSendGridTemplateDetails(
          flight.email_template_id as string,
        );
        if (cancelled) {
          return;
        }
        setSelectedEmailTemplate(details);
        const capabilities = detectTemplateCapabilities(details.merge_fields);
        setTemplateSupportsVoucher(capabilities.supports_voucher);
        setTemplateHasLandingPageLink(
          details.merge_fields
            .map((f) => f.toLowerCase())
            .includes("landing_page_link"),
        );
        setTemplateHasInviteLink(templateUsesInviteLink(details.merge_fields));
      } catch (err) {
        console.error(
          "Failed to load email template details for edit-mode hydrate:",
          err,
        );
        // Leave selectedEmailTemplate null; operator will see the empty
        // template state and can re-pick. We keep formData.email_template_id
        // unchanged so the existing reference isn't lost on save unless the
        // operator explicitly picks a different template or clears it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flight?.email_template_id]);

  // EIB silent-blast guard resolver. Mirrors send-campaign-flight's invite-token
  // gate EXACTLY: the send path tokenizes {{invite_link}} only when the flight's
  // effective offer is a $0 placeholder bound to an event whose
  // event_ticketing_provisioning.registration_mode === 'invite_gated'. For a
  // reminder the "effective offer" is the source flight's offer (the send path
  // resolves it off voucher_source_flight_id). We resolve willTokenize the same
  // way so the UI never blocks a flight the send path would actually tokenize,
  // and never green-lights one it would blast tokenless.
  useEffect(() => {
    // Only invite_link templates can trigger the blast; skip the lookup otherwise.
    if (!templateHasInviteLink) {
      setInviteContext({ loaded: true, willTokenize: false });
      return;
    }
    const effectiveOfferId = isReminder
      ? (siblingFlights.find((f) => f.id === reminderSourceFlightId)
          ?.offer_id ?? null)
      : formData.offer_id || null;
    if (!effectiveOfferId) {
      setInviteContext({ loaded: true, willTokenize: false });
      return;
    }
    let cancelled = false;
    setInviteContext({ loaded: false, willTokenize: false });
    (async () => {
      try {
        const { data: offer } = await supabase
          .from("offers")
          .select("is_placeholder, event_id")
          .eq("id", effectiveOfferId)
          .maybeSingle();
        let prov: { registration_mode?: string | null } | null = null;
        if (offer?.is_placeholder === true && offer?.event_id) {
          const res = await supabase
            .from("event_ticketing_provisioning")
            .select("registration_mode")
            .eq("event_id", offer.event_id as string)
            .maybeSingle();
          prov = res.data;
        }
        if (!cancelled) {
          setInviteContext({
            loaded: true,
            willTokenize: willTokenizeInviteLink(offer, prov),
          });
        }
      } catch (err) {
        console.error(
          "Failed to resolve invite-link tokenization context:",
          err,
        );
        // Leave loaded=false so the schedule-time guard neither blocks nor
        // green-lights on an unresolved lookup (the send path stays authoritative).
        if (!cancelled) {
          setInviteContext({ loaded: false, willTokenize: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    templateHasInviteLink,
    isReminder,
    reminderSourceFlightId,
    formData.offer_id,
    siblingFlights,
  ]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      const [smsTemplatesList, offers, attributes, activeOffers, groups] =
        await Promise.all([
          getSMSTemplates(),
          getCampaignOffers(campaignId),
          getAttributeDefinitions(),
          getAvailableOffers(),
          listActiveGroups().catch(() => [] as SubscriptionGroup[]),
        ]);
      setSubscriptionGroups(groups);
      setSmsTemplates(smsTemplatesList);
      setAvailableOffers(
        activeOffers.map((o) => ({
          id: o.id,
          name: o.name,
          value_amount: o.value_amount ?? undefined,
        })),
      );
      setCampaignOffers(offers);
      setActiveAttributes(attributes.filter((d) => d.is_active));
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoadingData(false);
    }
  };

  // Composed audience-filter field list per R6 of the 2026-05-28 audit
  // verdict. The static AUDIENCE_FILTER_FIELDS hardcoded set is unioned
  // with every active custom attribute, mapped to its FilterFieldType.
  // EVERY consumer of the filter field list (selector dropdown, operator
  // lookup, condition summary, validation messages, rehydration) reads
  // from this memo, so adding a custom-attribute slug to
  // contact_attribute_definitions surfaces it everywhere or nowhere.
  const audienceFilterFields = useMemo(
    () => [
      ...STATIC_AUDIENCE_FILTER_FIELDS,
      ...activeAttributes.map((d) => ({
        value: `${CUSTOM_ATTRIBUTE_PREFIX}${d.slug}`,
        label: d.name,
        type: customAttrDataTypeToFilterType(d.data_type),
        group: "Custom Attributes",
      })),
    ],
    [activeAttributes],
  );

  // Combined contact-field options for the MergeFieldMapper dropdown:
  // standard CONTACT_FIELDS plus any active custom attributes, prefixed
  // with "custom:" so the send-path resolver can distinguish them from
  // standard contact columns.
  const mapperContactFields = useMemo(
    () => [
      ...CONTACT_FIELDS,
      ...activeAttributes.map((d) => ({
        value: `${CUSTOM_ATTRIBUTE_PREFIX}${d.slug}`,
        label: `Attribute: ${d.name}`,
      })),
    ],
    [activeAttributes],
  );

  // Single LandingConfig object assembled from the fixed string-map, the
  // prefill toggle, and the custom-param rows — the one shape the shared
  // landingLink utils (preview + validation) and the persisted
  // metadata.landing_page_config both use.
  const effectiveLandingConfig = useMemo<LandingConfig>(
    () => ({
      enabled: landingPageEnabled,
      ...landingPageConfig,
      prefill_registration: landingPagePrefill,
      custom_params: customParams,
    }),
    [landingPageEnabled, landingPageConfig, landingPagePrefill, customParams],
  );

  // Live preview of the landing-page URL — shared, fragment-safe builder that
  // keeps {{placeholders}} legible (no contact selected here). Same param
  // ordering/precedence the backend applies at send time.
  const landingPagePreviewUrl = useMemo(
    () => previewLandingUrl(effectiveLandingConfig),
    [effectiveLandingConfig],
  );

  // Inline validation errors for the landing panel (blank/invalid base URL,
  // unknown placeholders, partial/duplicate/colliding custom rows). Shown in
  // the panel while editing; validate() blocks SCHEDULING on these.
  const landingValidationErrors = useMemo(
    () => validateLandingConfig(effectiveLandingConfig),
    [effectiveLandingConfig],
  );

  const addCustomParam = () =>
    setCustomParams((prev) => [...prev, { name: "", value: "" }]);
  const updateCustomParam = (
    index: number,
    field: "name" | "value",
    value: string,
  ) =>
    setCustomParams((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  const removeCustomParam = (index: number) =>
    setCustomParams((prev) => prev.filter((_, i) => i !== index));
  const toggleCustomParamRequired = (index: number) =>
    setCustomParams((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, required: !row.required } : row,
      ),
    );

  // Live summary sentence for audience filter section
  const audienceFilterSummary = useMemo(() => {
    if (!audienceFiltersEnabled || audienceFilterConditions.length === 0) {
      return "";
    }
    const conjunction = audienceFiltersMatch === "all" ? "ALL" : "ANY";
    const parts = audienceFilterConditions.map((c) => {
      const fieldDef = audienceFilterFields.find((f) => f.value === c.field);
      const fieldLabel = fieldDef?.label ?? c.field;
      const ops =
        OPERATORS_BY_TYPE[(fieldDef?.type ?? "text") as FilterFieldType] ?? [];
      const opMeta = ops.find((o) => o.value === c.operator);
      const opLabel = opMeta?.label ?? c.operator;
      const shape = opMeta?.valueShape ?? "scalar";
      const fieldType = (fieldDef?.type ?? "text") as FilterFieldType;
      if (shape === "multi") {
        const list = c.values ?? [];
        return list.length
          ? `${fieldLabel} ${opLabel} [${list.join(", ")}]`
          : `${fieldLabel} ${opLabel}`;
      }
      // Tag scalar (does_not_have): value lives in values[0] to match the
      // edge evaluator's storage convention. Non-tag scalars use value.
      if (shape === "scalar" && fieldType === "tag") {
        const v = c.values?.[0];
        return v
          ? `${fieldLabel} ${opLabel} "${v}"`
          : `${fieldLabel} ${opLabel}`;
      }
      return c.value
        ? `${fieldLabel} ${opLabel} "${c.value}"`
        : `${fieldLabel} ${opLabel}`;
    });
    return `At send time, contacts must match ${conjunction} of: ${parts.join("; ")}.`;
  }, [audienceFiltersEnabled, audienceFiltersMatch, audienceFilterConditions]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTemplateSelect = (template: SendGridTemplateDetails) => {
    setSelectedEmailTemplate(template);
    // Auto-copy the template's stored subject into the per-flight override
    // ONLY when it's a real subject string. A BARE self-referential template
    // ({{{subject}}}) has no default — copying it back into the override creates
    // a loop that resolves to empty at send time, so the operator must enter a
    // real subject. But SendGrid's overridable idiom
    // {{#if subject}}{{subject}}{{else}}Default{{/if}} carries a default in its
    // {{else}} branch — auto-copy that literal instead of leaving it blank.
    const templateSubject = template.version.subject;
    const derivedSubject = extractOverridableSubjectFallback(templateSubject);
    const safeAutoCopy =
      derivedSubject !== null
        ? derivedSubject
        : templateSubject && !isSelfReferentialSubject(templateSubject)
          ? templateSubject
          : null;
    setFormData((prev) => ({
      ...prev,
      email_template_id: template.id,
      email_subject: safeAutoCopy ?? prev.email_subject,
    }));

    // Auto-detect capabilities
    const capabilities = detectTemplateCapabilities(template.merge_fields);
    setTemplateSupportsVoucher(capabilities.supports_voucher);
    setTemplateHasLandingPageLink(
      template.merge_fields
        .map((f) => f.toLowerCase())
        .includes("landing_page_link"),
    );
    setTemplateHasInviteLink(templateUsesInviteLink(template.merge_fields));

    // Upsert to template registry (fire and forget)
    upsertTemplateRegistry(template).catch((err) =>
      console.warn("Failed to upsert template registry:", err),
    );

    // Initialize field mappings. A "Landing page link" mapping is carried over
    // when the new template uses the same variable, so duplicating a flight and
    // picking the next template keeps the landing page link source (and with it
    // metadata.landing_page_config on save). Everything else resets, as before.
    // Always re-initialise, INCLUDING a template with zero merge fields (2026-10-14,
    // landing-link audit P3): skipping the empty case kept the previous template's
    // mappings — and with a landing_page mapping, its landing_page_config on save.
    setFieldMappings((prev) =>
      initialMappingsForTemplate(template.merge_fields ?? [], prev),
    );
  };

  const clearEmailTemplate = () => {
    setSelectedEmailTemplate(null);
    setTemplateSupportsVoucher(false);
    setTemplateHasLandingPageLink(false);
    setTemplateHasInviteLink(false);
    setFormData((prev) => ({
      ...prev,
      email_template_id: "",
    }));
    setFieldMappings([]);
  };

  // Event campaigns: a NEW flight defaults to the "Events" subscription category (SendGrid ASM
  // 35127). Editing an existing flight never changes its saved category.
  useEffect(() => {
    if (flight || !isEventCampaign || eventsGroupDefaultedRef.current) {
      return;
    }
    if (formData.subscription_group_id) {
      eventsGroupDefaultedRef.current = true;
      return;
    }
    const events = subscriptionGroups.find(
      (g) => g.sendgrid_group_id === 35127 || g.slug === "events",
    );
    if (!events) {
      return;
    }
    eventsGroupDefaultedRef.current = true;
    setEventsGroupDefaulted(true);
    setFormData((prev) =>
      prev.subscription_group_id
        ? prev
        : { ...prev, subscription_group_id: events.id },
    );
  }, [
    flight,
    isEventCampaign,
    subscriptionGroups,
    formData.subscription_group_id,
  ]);

  // SendGrid's own raw unsubscribe tags in the selected template (2026-09-30). Every send path
  // refuses these, so the operator is told here, before scheduling.
  const rawSendgridUnsubTags =
    channel === "email"
      ? detectRawSendgridUnsubscribeTags(selectedEmailTemplate?.html_content)
      : [];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Flight name is required";
    }

    if (isReminder) {
      // A reminder inherits the source flight's list + vouchers — no segment is
      // picked here. It just needs a source flight that has a list to inherit.
      if (!reminderSourceFlightId) {
        newErrors.reminder_source =
          "Select the original voucher flight this reminder reuses.";
      } else if (
        !siblingFlights.find((f) => f.id === reminderSourceFlightId)?.list_id
      ) {
        newErrors.reminder_source =
          "That source flight has no segment to inherit. Pick a flight that targets a segment.";
      }
    } else if (!formData.list_id) {
      newErrors.list_id = "Please select a segment";
    } else if (staleListId && staleListId === formData.list_id) {
      // Don't silently preserve a stale list_id on save: the segment
      // the flight pointed at was deleted, and the user must pick a
      // replacement before save/schedule succeeds.
      newErrors.list_id =
        "The previously selected segment no longer exists. Please select a current segment.";
    }

    // Voucher template is now optional. A reminder doesn't generate (it reuses), so
    // the offer requirement doesn't apply.
    if (!isReminder && generateVouchers && !formData.offer_id) {
      newErrors.generate_vouchers =
        "Select an Associated Offer below — the voucher value/name come from it.";
    }

    // Reply-To override (optional): if set, must be a valid email so we never
    // ship a malformed reply_to to SendGrid. Empty → no override (default).
    // {{assigned_agent}} is the only variable accepted (mig 1550).
    if (channel === "email" && replyTo.trim()) {
      const rc = checkReplyToValue(replyTo, { allowConcierge: true });
      if (!rc.ok) {
        newErrors.reply_to = rc.error;
      } else if (
        fromVariable &&
        replyToVariable &&
        fromVariable !== replyToVariable
      ) {
        // One variable per flight (the send path resolves one lookup per batch).
        newErrors.reply_to = `From uses ${SENDER_VARIABLE_TOKEN[fromVariable]}; use the same variable here, or a normal email address.`;
      }
    }
    if (channel === "email" && replyToIsAgent && replyToFallback.trim()) {
      const fc = checkFallbackReplyTo(replyToFallback);
      if (!fc.ok) {
        newErrors.reply_to_fallback = fc.error;
      }
    }

    // Validate selected channel has a template
    if (channel === "email" && !formData.email_template_id) {
      newErrors.email_template_id = "Please select an email template";
    }
    if (channel === "sms" && !formData.sms_template_id) {
      newErrors.sms_template_id = "Please select an SMS template";
    }

    // Subject-override validation. Self-reference (e.g. operator put
    // {{subject}} in the field) is a hard error — would resolve to empty
    // string at send time and produce a blank-subject email.
    if (channel === "email" && formData.email_subject?.trim()) {
      const knownVars = new Set<string>([
        ...(selectedEmailTemplate?.merge_fields ?? []),
        // Standard fields renderTemplateData always sets:
        "email",
        "first_name",
        "last_name",
        // Phase 2 auto-injected:
        "preheader",
      ]);
      const { hardError } = validateSubjectOverride(
        formData.email_subject,
        knownVars,
      );
      if (hardError) {
        newErrors.email_subject = hardError;
      }
    }

    // Strict-on-save gate (2026-05-14): if the operator is staging a
    // schedule, every template merge field must have a resolution at
    // send time. Mirrors the same check PreSendConfirmationModal applies
    // when the operator clicks Schedule on the flight detail view —
    // before this gate the two surfaces disagreed: edit-flight save would
    // accept the schedule, then the scheduler would refuse it.
    if (
      channel === "email" &&
      formData.scheduled_send_at &&
      selectedEmailTemplate?.merge_fields?.length
    ) {
      // SYSTEM_FALLBACK_FIELDS covers contact-column fallbacks
      // (email/first_name/last_name). SENDGRID_AUTO_INJECTED_FIELDS covers
      // template variables SendGrid resolves at send time via the ASM group
      // (unsubscribe, unsubscribe_preferences, weblink, sender_*). Operators
      // legitimately leave the latter unmapped; flagging them as unmapped
      // would block schedule on every newsletter template (operator-flagged
      // 2026-05-28).
      const autoInjected = new Set<string>([
        ...SYSTEM_FALLBACK_FIELDS,
        ...SENDGRID_AUTO_INJECTED_FIELDS,
      ]);
      if (formData.email_subject?.trim()) {
        autoInjected.add("subject");
      }
      if (preheader.trim()) {
        autoInjected.add("preheader");
      }
      const unmapped = computeUnmappedTemplateVariables(
        selectedEmailTemplate.merge_fields,
        fieldMappings,
        autoInjected,
      );
      if (unmapped.length > 0) {
        newErrors.scheduled_send_at = `Cannot schedule — these template variables aren't mapped: ${unmapped.join(", ")}. Map them above or clear the schedule to save as draft.`;
      }
    }

    // Broken legacy unsubscribe tokens (audit R2-H2): a template containing
    // {{unsubscribe}} / {{unsubscribe_preferences}} ships a dead unsubscribe link on an
    // API send. Block whenever an email template with these tokens is selected — draft OR
    // schedule — so the broken template can't be saved and later sent.
    // FLAG-GATED (audit FG-B1): this hard block only applies when the template guard is ON.
    // While OFF (deploy-dark default) legacy {{unsubscribe}} templates must still save/send —
    // a non-blocking migration warning is shown instead (see templateGuardWarning below).
    if (
      templateGuardEnabled &&
      channel === "email" &&
      selectedEmailTemplate?.merge_fields?.length
    ) {
      const forbidden = detectForbiddenUnsubscribeTokens(
        selectedEmailTemplate.merge_fields,
      );
      if (forbidden.length > 0) {
        newErrors.email_template_id = forbiddenUnsubscribeMessage(forbidden);
      }
    }

    // Scanner-triggered unsubscribes (2026-09-30): SendGrid's raw unsubscribe tags unsubscribe on
    // a bare page load. The send is refused server-side regardless of any flag, so block
    // SCHEDULING here too; a draft can still be saved while the template is being fixed.
    if (formData.scheduled_send_at && rawSendgridUnsubTags.length > 0) {
      newErrors.email_template_id = rawSendgridUnsubscribeMessage(
        rawSendgridUnsubTags,
        selectedEmailTemplate?.name,
      );
    }

    // EIB silent-blast guard (companion to project_eib_bulk_invite_flight):
    // block SCHEDULING an {{invite_link}} template when the send path won't
    // tokenize it (offer isn't a placeholder ticket for an invite_gated event).
    // Otherwise the whole audience receives an empty/tokenless invite link and
    // the flight reports all-sent. Draft saves are still allowed (operator may
    // still be attaching the ticket offer / enabling invite-gating); we only
    // fail when a send is actually being staged. We require inviteContext.loaded
    // so an unresolved lookup never false-blocks a legitimate flight.
    if (
      channel === "email" &&
      formData.scheduled_send_at &&
      templateHasInviteLink &&
      inviteContext.loaded &&
      !inviteContext.willTokenize
    ) {
      newErrors.email_template_id =
        "This template uses {{invite_link}}, but this flight won't generate personalized invite links — its offer isn't an admission ticket for an invite-gated event, so every recipient would get an empty link. Attach the event's ticket offer and set the event's registration to invite-gated, or choose a template without {{invite_link}} before scheduling.";
    }

    // Landing-page config validation. Draft saves stay permissive (operator
    // may still be composing the URL); block only when actually staging a
    // schedule so a known-broken link (missing base URL, unknown {{var}},
    // partial/duplicate/colliding custom row) never reaches a real send.
    if (
      channel === "email" &&
      formData.scheduled_send_at &&
      landingPageEnabled &&
      landingValidationErrors.length > 0
    ) {
      newErrors.landing_page_config = `Cannot schedule — fix the landing page link: ${landingValidationErrors.join(" ")}`;
    }

    // Audience filter validation
    if (audienceFiltersEnabled) {
      if (audienceFilterConditions.length === 0) {
        newErrors.audience_filters = "Add at least one condition.";
      } else {
        for (let i = 0; i < audienceFilterConditions.length; i++) {
          const c = audienceFilterConditions[i];
          const fieldDef = audienceFilterFields.find(
            (f) => f.value === c.field,
          );
          const fieldType = (fieldDef?.type ?? "text") as FilterFieldType;
          const ops = OPERATORS_BY_TYPE[fieldType] ?? [];
          const opDef = ops.find((o) => o.value === c.operator);
          // Tag operators (scalar AND multi) all serialize to values[].
          // Non-tag scalar operators use value (string).
          if (opDef?.valueShape === "scalar" && fieldType === "tag") {
            const v = c.values?.[0]?.trim() ?? "";
            if (!v || (c.values?.length ?? 0) !== 1) {
              newErrors.audience_filters = `Condition ${i + 1}: pick exactly one tag for "${opDef.label}".`;
              break;
            }
          } else if (opDef?.valueShape === "scalar" && !c.value.trim()) {
            newErrors.audience_filters = `Condition ${i + 1}: enter a value for "${opDef.label}".`;
            break;
          } else if (
            opDef?.valueShape === "multi" &&
            (!c.values || c.values.length === 0)
          ) {
            newErrors.audience_filters = `Condition ${i + 1}: pick at least one tag for "${opDef.label}".`;
            break;
          }
        }
      }
    }

    // Content-mapping validation (audit-AI 2026-06-04 R3): block save
    // whenever any sourceType='content' mapping points at a missing /
    // private / non-image / no-storage-URL Content Item. Reading from
    // the background-validated state populated by the contentMappingIds
    // effect above.
    if (contentMappingErrors.length > 0) {
      const summary = contentMappingErrors
        .map((e) => `${e.label}`)
        .filter((v, i, a) => a.indexOf(v) === i)
        .join("; ");
      newErrors.email_template_id = `Content Item mapping issue: ${summary}. Pick a valid IMAGE Content Item.`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Build scheduled_send_at as ISO string with timezone offset
    let scheduledSendAt: string | undefined;
    if (formData.scheduled_send_at) {
      const offset = getTimezoneOffset(
        sendTimezone,
        formData.scheduled_send_at,
      );
      scheduledSendAt = `${formData.scheduled_send_at}:00${offset}`;
    }

    // Reminder flights (Fix E): inherit the source flight's list (no separate segment),
    // reuse its vouchers, and generate nothing. voucher_source_flight_id is sent either way
    // (null when not a reminder) so toggling it off on edit clears the link.
    const reminderSource = isReminder
      ? siblingFlights.find((f) => f.id === reminderSourceFlightId)
      : undefined;
    const effectiveListId = isReminder
      ? reminderSource?.list_id || undefined
      : formData.list_id || undefined;

    const submitData: CreateFlightDTO = {
      campaign_id: campaignId,
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      list_id: effectiveListId,
      // Vouchers now derive from the linked offer (no separate voucher template).
      // A reminder never generates (it reuses the source's codes).
      generate_vouchers: isReminder ? false : generateVouchers,
      voucher_source_flight_id: isReminder ? reminderSourceFlightId : null,
      offer_id: formData.offer_id || undefined,
      // Only include the selected channel's data
      sms_template_id:
        channel === "sms" ? formData.sms_template_id || undefined : undefined,
      email_subject:
        channel === "email"
          ? formData.email_subject.trim() || undefined
          : undefined,
      scheduled_send_at: scheduledSendAt,
      email_template_id:
        channel === "email"
          ? formData.email_template_id || undefined
          : undefined,
      email_template_data:
        channel === "email" && formData.email_template_id
          ? {
              field_mappings: fieldMappings,
              template_name: selectedEmailTemplate?.name,
            }
          : undefined,
      subscription_group_id:
        channel === "email"
          ? formData.subscription_group_id || null
          : undefined,
      // Always sent, so an existing flight edited without touching this keeps its value and a new
      // flight is explicitly 'default' rather than relying on the column default alone.
      sendgrid_ip_pool_key: formData.sendgrid_ip_pool_key || "default",
      metadata: {
        ...(formData.scheduled_send_at ? { send_timezone: sendTimezone } : {}),
        ...(audienceMode === "retarget" && retargetFlightId
          ? {
              retarget_flight_id: retargetFlightId,
              retarget_exclude_event: retargetExcludeEvent,
            }
          : {}),
        ...(applyTag.trim() ? { apply_tag: applyTag.trim() } : {}),
        ...(removeTag.trim() ? { remove_tag: removeTag.trim() } : {}),
        ...(preheader.trim() ? { preheader: preheader.trim() } : {}),
        ...(isReminder && reminderIncludeNewMembers
          ? { reminder_include_new_members: true }
          : {}),
        ...(sanitizeSenderName(fromName)
          ? { from_name: sanitizeSenderName(fromName) }
          : {}),
        ...(fromEmail && fromEmail !== DEFAULT_FROM_EMAIL
          ? { from_email: fromEmail }
          : {}),
        // {{assigned_agent}}: the fallback sender is always stored (required; preselected to the default).
        ...(fromIsAgent ? { from_fallback_email: fromFallbackEmail } : {}),
        ...(channel === "email" && replyTo.trim()
          ? {
              reply_to: {
                email: replyToVariable
                  ? SENDER_VARIABLE_TOKEN[replyToVariable]
                  : replyTo.trim(),
              },
            }
          : {}),
        ...(channel === "email" && replyToIsAgent && replyToFallback.trim()
          ? { reply_to_fallback: { email: replyToFallback.trim() } }
          : {}),
        // Blank name -> no key, so the flight saves exactly as before (2026-11-04).
        ...(channel === "email" &&
        replyTo.trim() &&
        sanitizeSenderName(replyToName)
          ? { reply_to_name: sanitizeSenderName(replyToName) }
          : {}),
        ...(landingPageEnabled
          ? {
              landing_page_config: {
                enabled: true,
                ...landingPageConfig,
                prefill_registration: landingPagePrefill,
                // Persist only cleaned rows (blank rows dropped), order kept.
                // Omit the key entirely when empty so old flights stay byte-
                // identical and don't gain an empty array.
                ...(normalizeCustomParams(customParams).length > 0
                  ? { custom_params: normalizeCustomParams(customParams) }
                  : {}),
              },
            }
          : {}),
        ...(audienceFiltersEnabled
          ? {
              audience_filters: {
                enabled: true,
                match: audienceFiltersMatch,
                conditions: audienceFilterConditions,
              },
            }
          : {}),
        // Per-flight risky opt-out. Persist ONLY when true so flights that never touch it
        // stay byte-identical (absent === risky included === today's behaviour).
        ...(excludeRisky ? { exclude_risky: true } : {}),
      },
    } as CreateFlightDTO;

    onSubmit(submitData);
  };

  if (loadingData) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
        <p className="text-gray-600 mt-4">Loading templates...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Flight Name */}
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Flight Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            errors.name ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Spring 2026 Email Blast"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Brief description of this flight"
        />
      </div>

      {/* Reminder flight (Fix E): reuse an earlier voucher flight's codes + list */}
      <div className="rounded-lg border border-gray-200 p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isReminder}
            onChange={(e) => setIsReminder(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">
            This is a reminder for an earlier voucher flight
          </span>
        </label>
        <p className="mt-1 ml-6 text-xs text-gray-500">
          Re-sends the SAME voucher codes (no new codes) to the original
          flight's list. The segment and voucher settings are inherited from the
          source flight.
        </p>
        {isReminder && (
          <div className="mt-3 ml-6 space-y-1">
            <label className="block text-xs font-medium text-gray-600">
              Source voucher flight <span className="text-red-500">*</span>
            </label>
            {(() => {
              const sourceCandidates = siblingFlights.filter((f) =>
                isReminderSourceCandidate(f, flight?.id),
              );
              if (sourceCandidates.length === 0) {
                return (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    No eligible source voucher flights found for this campaign.
                    A source must be a non-reminder flight that targets a
                    segment and has vouchers to reuse (the original voucher
                    email).
                  </p>
                );
              }
              return (
                <select
                  value={reminderSourceFlightId}
                  onChange={(e) => setReminderSourceFlightId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">
                    Select the original voucher flight...
                  </option>
                  {sourceCandidates.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                      {f.status ? ` (${f.status})` : ""}
                    </option>
                  ))}
                </select>
              );
            })()}
            {errors.reminder_source && (
              <p className="text-xs text-red-600">{errors.reminder_source}</p>
            )}
            <p className="text-xs text-gray-500">
              Each recipient gets the same code/QR they received from this
              flight.
            </p>
            <label className="mt-2 flex items-start gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={reminderIncludeNewMembers}
                onChange={(e) => setReminderIncludeNewMembers(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span>
                Include new segment members added since the original send
                <span className="block text-gray-500">
                  Off (default): remind only the original recipients. On: also
                  send to members added to the list since the original send. New
                  members without an existing voucher are skipped on voucher
                  reminders.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Segment Selection — hidden for reminders (the source flight's list is used) */}
      {!isReminder && (
        <div>
          <label
            htmlFor="list_id"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Target Segment <span className="text-red-500">*</span>
          </label>
          <ListAutocomplete
            value={formData.list_id}
            onChange={(listId) => {
              // Picking a new segment clears any stale-id flag for the
              // previously-saved value.
              if (staleListId && listId && listId !== staleListId) {
                setStaleListId(null);
              }
              handleChange("list_id", listId);
            }}
            onStaleValue={(staleId) => setStaleListId(staleId)}
            placeholder="Search for a segment..."
            error={errors.list_id}
            disabled={loading}
            // #1b: dynamic segments are allowed for NON-voucher flights — at send time the audience is
            // evaluated from the segment criteria (stream_dynamic_segment_page) and frozen into the
            // recipient snapshot. Voucher flights still require a static segment (no snapshot-first
            // minting yet), enforced by the DB trigger; keep the picker static-only when vouchers are on.
            staticOnly={generateVouchers}
          />
          <p className="mt-1 text-xs text-gray-500">
            Select the contact segment to target with this flight.
            {generateVouchers
              ? " Voucher flights require a static segment."
              : " Dynamic segments are evaluated at send time."}
          </p>
          {/* A1: combine several segments into one deduped static list, then target it. The combined
              output is always static, so it's valid for voucher flights too. */}
          <button
            type="button"
            onClick={() => setShowCombineModal(true)}
            disabled={loading}
            className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            + Combine segments…
          </button>
          <CombineSegmentsModal
            isOpen={showCombineModal}
            onClose={() => setShowCombineModal(false)}
            onCreated={(list) => {
              setStaleListId(null);
              handleChange("list_id", list.id);
            }}
          />
        </div>
      )}

      {/* Target Audience — Retarget Option */}
      {siblingFlights.filter((f) => f.status === "sent").length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="audience_mode"
                checked={audienceMode === "full"}
                onChange={() => {
                  setAudienceMode("full");
                  setRetargetFlightId("");
                }}
                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Full Segment
                <span className="ml-1 text-xs text-gray-500">
                  (all contacts in selected segment)
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="audience_mode"
                checked={audienceMode === "retarget"}
                onChange={() => setAudienceMode("retarget")}
                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Retarget from Previous Flight
                <span className="ml-1 text-xs text-gray-500">
                  (follow up with non-converters)
                </span>
              </span>
            </label>
          </div>

          {audienceMode === "retarget" && (
            <div className="mt-3 ml-6 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Source Flight
                </label>
                <select
                  value={retargetFlightId}
                  onChange={(e) => setRetargetFlightId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a sent flight...</option>
                  {siblingFlights
                    .filter((f) => f.status === "sent")
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                        {f.sent_at
                          ? ` (sent ${new Date(f.sent_at).toLocaleDateString()})`
                          : ""}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Include contacts who
                </label>
                <select
                  value={retargetExcludeEvent}
                  onChange={(e) => setRetargetExcludeEvent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {RETARGET_EXCLUDE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Channel Selection */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Channel <span className="text-red-500">*</span>
        </h3>
        <div className="flex gap-4">
          <label
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
              channel === "email"
                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                : "border-gray-300 hover:bg-gray-50"
            }`}
          >
            <input
              type="radio"
              name="channel"
              value="email"
              checked={channel === "email"}
              onChange={() => setChannel("email")}
              className="text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Email</span>
              <p className="text-xs text-gray-500">Send via SendGrid</p>
            </div>
          </label>
          <label
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
              channel === "sms"
                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                : "border-gray-300 hover:bg-gray-50"
            }`}
          >
            <input
              type="radio"
              name="channel"
              value="sms"
              checked={channel === "sms"}
              onChange={() => setChannel("sms")}
              className="text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">SMS</span>
              <p className="text-xs text-gray-500">Send via Sinch</p>
            </div>
          </label>
        </div>
      </div>

      {/* Optional campaign-offer association — only when NOT generating vouchers.
          The voucher case has its own REQUIRED offer selector co-located with the
          "Generate vouchers" toggle below (so there's no scroll up to pick it). */}
      {!generateVouchers && campaignOffers.length > 0 && (
        <div>
          <label
            htmlFor="offer_id"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Associated Offer
          </label>
          <select
            id="offer_id"
            value={formData.offer_id}
            onChange={(e) => handleChange("offer_id", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">No offer (informational email)</option>
            {campaignOffers.map((co) => (
              <option key={co.offer_id} value={co.offer_id}>
                {co.offer.name}
                {co.offer.value_amount ? ` ($${co.offer.value_amount})` : ""}
                {!co.offer.is_active ? " (inactive)" : ""}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Link this flight to a specific offer from the campaign
          </p>
        </div>
      )}

      {/* Template Capability Warning */}
      {formData.offer_id &&
        generateVouchers &&
        channel === "email" &&
        selectedEmailTemplate &&
        !templateSupportsVoucher && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div className="text-sm text-amber-800">
                <p className="font-medium">
                  Template does not have voucher fields
                </p>
                <p className="text-xs mt-0.5">
                  This template doesn't include <code>voucher_code</code> or{" "}
                  <code>qr_image_url</code> merge fields. Recipients will get
                  vouchers but the email won't display them.
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Email Configuration — only when email channel selected */}
      {channel === "email" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Template <span className="text-red-500">*</span>
            </label>
            {selectedEmailTemplate ? (
              <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedEmailTemplate.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Subject:{" "}
                      {selectedEmailTemplate.version.subject || "(No subject)"}
                    </p>
                    {selectedEmailTemplate.merge_fields.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedEmailTemplate.merge_fields
                          .slice(0, 5)
                          .map((field) => (
                            <span
                              key={field}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-blue-50 text-blue-700"
                            >
                              {field}
                            </span>
                          ))}
                        {selectedEmailTemplate.merge_fields.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{selectedEmailTemplate.merge_fields.length - 5}{" "}
                            more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(() => {
                      // Preview button is disabled when neither html_content
                      // nor preview is available on the loaded template
                      // details — TemplatePreviewModal would render an empty
                      // body otherwise. No on-demand fetch path exists today.
                      const hasPreviewContent = Boolean(
                        selectedEmailTemplate?.html_content ||
                          selectedEmailTemplate?.preview,
                      );
                      return (
                        <button
                          type="button"
                          onClick={() => setShowTemplatePreview(true)}
                          disabled={!hasPreviewContent}
                          title={
                            hasPreviewContent
                              ? undefined
                              : "Template has no HTML content available to preview"
                          }
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                        >
                          Preview
                        </button>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={() => setShowTemplateModal(true)}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={clearEmailTemplate}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowTemplateModal(true)}
                className={`w-full px-4 py-3 border-2 border-dashed rounded-lg text-center transition-colors ${
                  errors.email_template_id
                    ? "border-red-300 bg-red-50 hover:border-red-400"
                    : "border-gray-300 hover:border-primary-400 hover:bg-gray-50"
                }`}
              >
                <svg
                  className="mx-auto h-8 w-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="mt-2 block text-sm font-medium text-gray-700">
                  Select SendGrid Template
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  Choose from your SendGrid dynamic templates
                </span>
              </button>
            )}
            {errors.email_template_id && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email_template_id}
              </p>
            )}
            {/* Scanner-unsafe unsubscribe link (2026-09-30): always shown; scheduling is blocked
                in validate() and every send path refuses the template. */}
            {rawSendgridUnsubTags.length > 0 && !errors.email_template_id && (
              <div
                className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
                data-testid="raw-sendgrid-unsub-warning"
              >
                <span className="text-sm text-red-800">
                  {rawSendgridUnsubscribeMessage(
                    rawSendgridUnsubTags,
                    selectedEmailTemplate?.name,
                  )}
                </span>
              </div>
            )}
            {/* Dead-unsubscribe-link MIGRATION warning (ASM flag-gate, FG-B1): while the guard
                is OFF (deploy-dark) a legacy {{unsubscribe}} template still saves/sends, but we
                surface it so the owner migrates it to ASM tags before the guard is switched on.
                When the guard is ON this template is a hard block above, so we don't double-warn. */}
            {!templateGuardEnabled &&
              channel === "email" &&
              !!selectedEmailTemplate?.merge_fields?.length &&
              detectForbiddenUnsubscribeTokens(
                selectedEmailTemplate.merge_fields,
              ).length > 0 && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <span className="text-sm text-amber-800">
                    Heads up: this template still uses a legacy{" "}
                    <code>{"{{unsubscribe}}"}</code> link, which renders empty
                    on our sends. It will still send for now, but should be
                    changed to our unsubscribe link{" "}
                    <code>{"{{{crm_unsubscribe_url}}}"}</code>.
                  </span>
                </div>
              )}
            {/* EIB silent-blast warning — shown as soon as the operator picks an
                {{invite_link}} template whose send context won't tokenize it, so
                the problem is visible before they even try to schedule. Draft
                saves stay allowed; scheduling is blocked in validate(). */}
            {templateHasInviteLink &&
              inviteContext.loaded &&
              !inviteContext.willTokenize && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <svg
                    className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <p className="text-xs text-amber-800">
                    This template uses{" "}
                    <code className="rounded bg-amber-100 px-1 py-0.5">
                      {"{{invite_link}}"}
                    </code>
                    , but personalized invite links are only generated when the
                    flight's offer is an admission ticket for an{" "}
                    <strong>invite-gated</strong> event. As configured, every
                    recipient would receive an empty link. Attach the event's
                    ticket offer and set its registration to invite-gated, or
                    use a template without <code>{"{{invite_link}}"}</code>. You
                    can still save a draft — scheduling is blocked until this is
                    resolved.
                  </p>
                </div>
              )}
          </div>

          {/* Phase 2 — per-flight email content overrides. Only shown after
              a template is selected since the substituted-subject preview
              needs the template context. */}
          {selectedEmailTemplate && (
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900">
                Email content
              </h3>

              {/* Subject — editable per-flight override. */}
              <div>
                <label
                  htmlFor="email_subject"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Subject line
                </label>
                <input
                  id="email_subject"
                  type="text"
                  value={formData.email_subject}
                  onChange={(e) =>
                    handleChange("email_subject", e.target.value)
                  }
                  placeholder="e.g. Hi {{first_name}}, your voucher is ready"
                  maxLength={200}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 ${
                    errors.email_subject ? "border-red-400" : "border-gray-300"
                  }`}
                />
                {(() => {
                  // Render-time validation surface:
                  //   - hardError (self-reference) → red, also blocks save in validate()
                  //   - unknownVars (typos like {{first_nam}}) → amber soft warning
                  //   - empty + template uses {{{subject}}} → amber must-set hint
                  if (!formData.email_subject?.trim()) {
                    if (
                      isSelfReferentialSubject(
                        selectedEmailTemplate?.version.subject,
                      )
                    ) {
                      return (
                        <p className="mt-1 text-xs text-amber-700">
                          The selected template uses{" "}
                          <code className="font-mono">{"{{{subject}}}"}</code>{" "}
                          and requires a per-flight subject above. Without one,
                          recipients will see a blank subject.
                        </p>
                      );
                    }
                    return null;
                  }
                  const knownVars = new Set<string>([
                    ...(selectedEmailTemplate?.merge_fields ?? []),
                    "email",
                    "first_name",
                    "last_name",
                    "preheader",
                  ]);
                  const { hardError, unknownVars } = validateSubjectOverride(
                    formData.email_subject,
                    knownVars,
                  );
                  return (
                    <>
                      {hardError && (
                        <p className="mt-1 text-xs text-red-600">{hardError}</p>
                      )}
                      {!hardError && unknownVars.length > 0 && (
                        <p className="mt-1 text-xs text-amber-700">
                          Unknown variable
                          {unknownVars.length > 1 ? "s" : ""}:{" "}
                          {unknownVars.map((v) => `{{${v}}}`).join(", ")} —
                          these will render as empty strings at send time.
                        </p>
                      )}
                      {!hardError && (
                        <p className="mt-1 text-xs text-gray-500">
                          Preview:{" "}
                          <span className="font-medium text-gray-700">
                            {substituteHandlebars(formData.email_subject, {
                              first_name: "Jane",
                              last_name: "Sample",
                              email: "jane.sample@example.com",
                              voucher_code: "ABC123",
                              voucher_amount: "20",
                            })}
                          </span>
                        </p>
                      )}
                    </>
                  );
                })()}
                <p className="mt-1 text-xs text-gray-500">
                  Operator-set subject overrides the template default. Variables
                  substitute against contact data at send time.
                </p>
              </div>

              {/* Pre-header — inbox preview text. Operator's template needs
                  `{{preheader}}` in a hidden span for inboxes to show this. */}
              <div>
                <label
                  htmlFor="email_preheader"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Pre-header (inbox preview text)
                </label>
                <textarea
                  id="email_preheader"
                  value={preheader}
                  onChange={(e) => setPreheader(e.target.value)}
                  placeholder="Short preview text that appears next to the subject line in inboxes"
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional. Up to ~150 characters typically shown by inbox apps.
                  Your template HTML must include{" "}
                  <code className="font-mono">{"{{preheader}}"}</code> in a
                  hidden span for this to render.
                </p>
              </div>

              {/* Delivery IP pool. This is the SENDING IP, a different concept from the
                  send-from domain / sender identity below — a flight can warm a new IP
                  while keeping its existing From address. Locked once the flight leaves
                  draft: a large send spans several worker invocations and must not
                  straddle two IPs. */}
              <div>
                <label
                  htmlFor="sendgrid_ip_pool_key"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Delivery IP pool
                </label>
                <select
                  id="sendgrid_ip_pool_key"
                  value={formData.sendgrid_ip_pool_key}
                  disabled={!!flight && (flight.status ?? "draft") !== "draft"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sendgrid_ip_pool_key: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:bg-gray-50"
                >
                  <option value="default">
                    Current delivery pool (default)
                  </option>
                  <option value="usls_email_pool_01">
                    usls-email-pool-01 — warmup
                  </option>
                  <option value="usls_email_pool_02">
                    usls-email-pool-02 — warmup
                  </option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {formData.sendgrid_ip_pool_key === "usls_email_pool_01"
                    ? "Routes this flight through the dedicated warmup IP 159.183.13.156 (o1.newsletter.uslawshield.com) and automatically tags it with the category usls-email-pool-01-warmup so warmup volume can be reported on its own. A new IP has no sending reputation — keep the audience within the volume plan agreed with operations."
                    : formData.sendgrid_ip_pool_key === "usls_email_pool_02"
                      ? "Routes this flight through the dedicated warmup IP 159.183.87.246 and tags it usls-email-pool-02-warmup. Its reverse DNS is o2.newsletter01.2ainstitute.com — a 2A Institute domain, not uslawshield.com — so it builds reputation separately from pool 01 and suits 2A Institute mail. Sending uslawshield.com-branded mail from it aligns less well to receivers. Same per-flight warmup ceiling applies."
                      : !!flight && (flight.status ?? "draft") !== "draft"
                        ? "The delivery pool is fixed once a flight is scheduled or sending."
                        : "Sends from the pool this CRM already uses. Change this only to warm a new IP."}
                </p>
              </div>

              {/* Email subscription category (SendGrid ASM group). Attaches the
                  group so recipients can unsubscribe from THIS category (not all
                  mail). Inactive/absent until an admin configures groups. */}
              <div>
                <label
                  htmlFor="subscription_group_id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email subscription category
                </label>
                <select
                  id="subscription_group_id"
                  value={formData.subscription_group_id}
                  onChange={(e) => {
                    eventsGroupDefaultedRef.current = true;
                    setEventsGroupDefaulted(false);
                    setFormData((prev) => ({
                      ...prev,
                      subscription_group_id: e.target.value,
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">— None —</option>
                  {subscriptionGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                {eventsGroupDefaulted && (
                  <p className="mt-1 text-xs text-primary-700">
                    Set to Events because this campaign is for an event, so the
                    Unsubscribe button only stops event emails. You can change
                    it.
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.subscription_group_id
                    ? subscriptionGroups.find(
                        (g) => g.id === formData.subscription_group_id,
                      )?.description ||
                      "Recipients can unsubscribe from this category on SendGrid's hosted preferences page."
                    : subscriptionGroups.length === 0
                      ? "No subscription categories are configured yet."
                      : "Optional. Groups this email so recipients unsubscribe from the category, not all mail."}
                </p>
              </div>

              {/* Send-from domain. Choose which domain-authenticated identity
                  this flight sends from. Both are DKIM/SPF-aligned in SendGrid.
                  This selects the From identity only — it does NOT change the
                  outbound IP/pool (server-controlled), and both identities may
                  share the same IP. Not a load-balancing control. */}
              <div>
                <label
                  htmlFor="email_from_email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Send from
                </label>
                <select
                  id="email_from_email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  {SENDER_OPTIONS.map((o) => (
                    <option key={o.email} value={o.email}>
                      {o.label}
                    </option>
                  ))}
                  <option value={AGENT_FROM_OPTION.email}>
                    {AGENT_FROM_OPTION.label}
                  </option>
                  <option value={CONCIERGE_FROM_OPTION.email}>
                    {CONCIERGE_FROM_OPTION.label}
                  </option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  The From identity (domain) for this flight. Defaults to U.S.
                  LawShield; pick <strong>2A Institute</strong> to send from{" "}
                  <code className="font-mono">2ainstitute.com</code>. This
                  changes the From address and its sender reputation only — it
                  does not change the sending IP, so don't use it to spread
                  load.
                </p>
                {fromIsAgent && (
                  <div
                    className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3"
                    data-testid="from-agent-panel"
                  >
                    <p className="text-xs text-blue-900">
                      <strong>From: each recipient's {fromWho}.</strong>{" "}
                      {fromVariable === "assigned_concierge"
                        ? ASSIGNED_CONCIERGE_HELP
                        : ASSIGNED_AGENT_HELP}
                    </p>
                    <label
                      htmlFor="email_from_fallback_email"
                      className="mt-2 block text-sm font-medium text-gray-700 mb-1"
                    >
                      Fallback sender <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="email_from_fallback_email"
                      value={fromFallbackEmail}
                      onChange={(e) => setFromFallbackEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    >
                      {SENDER_OPTIONS.map((o) => (
                        <option key={o.email} value={o.email}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* From-name override — the display name shown in inboxes. Applies
                  on top of whichever sending domain is chosen above. */}
              <div>
                <label
                  htmlFor="email_from_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  From-name override
                </label>
                <input
                  id="email_from_name"
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder={
                    effectiveFixedFrom === "noreply@2ainstitute.com"
                      ? "2A Institute"
                      : "U.S. LawShield"
                  }
                  maxLength={50}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Display name shown in inboxes. Leave blank to use the sending
                  domain's default name.
                  {fromIsAgent &&
                    (fromVariable === "assigned_concierge"
                      ? ` ${FROM_NAME_CONCIERGE_HELP}`
                      : ' With the support agent as sender, agents are shown as "<Agent Name> | U.S. LawShield"; this name is used only for recipients who get the fallback sender.')}
                </p>
              </div>

              {/* Reply-To override. Empty by default → replies route to the
                  from address (noreply@uslawshield.com, the standard U.S.
                  LawShield reply target). Set per flight to redirect replies. */}
              <div>
                <label
                  htmlFor="email_reply_to"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reply-To override
                </label>
                <input
                  id="email_reply_to"
                  type="text"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder={`noreply@uslawshield.com (default), ${ASSIGNED_AGENT_VAR} or ${ASSIGNED_CONCIERGE_VAR}`}
                  maxLength={254}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 ${
                    errors.reply_to ? "border-red-400" : "border-gray-300"
                  }`}
                />
                {errors.reply_to ? (
                  <p className="mt-1 text-xs text-red-600">{errors.reply_to}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Where replies go. Leave blank and replies route to this
                    flight's sending address (
                    <code className="font-mono">
                      {fromIsAgent ? `the recipient's ${fromWho}` : fromEmail}
                    </code>
                    ). Set a per-flight address to route replies elsewhere (e.g.
                    a partner's inbox), or type{" "}
                    <code className="font-mono">{ASSIGNED_AGENT_VAR}</code> to
                    send replies to each recipient's support agent (or{" "}
                    <code className="font-mono">{ASSIGNED_CONCIERGE_VAR}</code>{" "}
                    for the concierge of their organization).
                  </p>
                )}
                {replyToIsAgent && (
                  <div
                    className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3"
                    data-testid="reply-to-agent-panel"
                  >
                    <p className="text-xs text-blue-900">
                      <strong>Reply-To: each recipient's {replyWho}.</strong>{" "}
                      {replyToVariable === "assigned_concierge"
                        ? ASSIGNED_CONCIERGE_HELP
                        : ASSIGNED_AGENT_HELP}
                    </p>
                    <label
                      htmlFor="email_reply_to_fallback"
                      className="mt-2 block text-sm font-medium text-gray-700 mb-1"
                    >
                      Fallback Reply-To (optional)
                    </label>
                    <input
                      id="email_reply_to_fallback"
                      type="email"
                      value={replyToFallback}
                      onChange={(e) => setReplyToFallback(e.target.value)}
                      placeholder="Blank: replies go to the sending address"
                      maxLength={254}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 ${
                        errors.reply_to_fallback
                          ? "border-red-400"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.reply_to_fallback && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.reply_to_fallback}
                      </p>
                    )}
                  </div>
                )}
                {replyTo.trim() && (
                  <div className="mt-3">
                    <label
                      htmlFor="email_reply_to_name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Reply-To name (optional)
                    </label>
                    <input
                      id="email_reply_to_name"
                      type="text"
                      value={replyToName}
                      onChange={(e) => setReplyToName(e.target.value)}
                      placeholder="e.g. U.S. LawShield Member Services"
                      maxLength={SENDER_NAME_MAX}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                    <p
                      className="mt-1 text-xs text-gray-500"
                      data-testid="reply-to-name-help"
                    >
                      {replyToVariable === "assigned_concierge"
                        ? REPLY_TO_NAME_CONCIERGE_HELP
                        : replyToIsAgent
                          ? REPLY_TO_NAME_AGENT_HELP
                          : REPLY_TO_NAME_HELP}{" "}
                      Quotes, angle brackets and braces are removed.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Merge Field Mapper */}
          {selectedEmailTemplate &&
            selectedEmailTemplate.merge_fields.length > 0 && (
              <div>
                {(generateVouchers || isReminder) && (
                  <div className="mb-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                    <strong>Voucher mapping required:</strong>{" "}
                    {isReminder
                      ? "this reminder re-sends each recipient's existing voucher, so map your template's voucher variable(s)"
                      : "this flight issues vouchers, so map your template's voucher variable(s)"}{" "}
                    (e.g. <code>{"{{voucher_code}}"}</code>,{" "}
                    <code>{"{{qr_image_url}}"}</code>) to the{" "}
                    <strong>Voucher</strong> source below — alongside your other
                    field mappings. Each recipient's unique code/QR is filled in
                    automatically at send.
                  </div>
                )}
                <MergeFieldMapper
                  mergeFields={selectedEmailTemplate.merge_fields}
                  mappings={fieldMappings}
                  onChange={setFieldMappings}
                  // Voucher source is needed both when this flight MINTS vouchers
                  // (generateVouchers) and when it's a REMINDER that re-sends an
                  // existing voucher (isReminder). Gating on generateVouchers alone
                  // made the Voucher source unreachable for reminder flights.
                  includeVoucherFields={generateVouchers || isReminder}
                  contactFields={mapperContactFields}
                />
              </div>
            )}

          {/* Landing Page Link config — visible whenever the mapper has a
              landing_page source mapping. The standalone checkbox was
              removed in favour of letting the mapper drive visibility:
              when an operator picks "Landing Page Link" as the source for a
              template variable, this config panel appears below the mapper
              with the URL-builder fields. The flight's metadata.landing_
              page_config is still the canonical persistence shape, so the
              server-side substitution path is unchanged. */}
          <div
            className={`border border-gray-200 rounded-lg p-4 ${
              landingPageEnabled ? "" : "hidden"
            }`}
          >
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                Landing page link config{" "}
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded text-gray-600">
                  {"{{landing_page_link}}"}
                </code>
              </h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Tracked URL built at send time and injected wherever the mapper
                has source set to <em>Landing Page Link</em>.
              </p>
            </div>

            {landingPageEnabled && (
              <div className="mt-4 space-y-4">
                {/* Template warning */}
                {selectedEmailTemplate && !templateHasLandingPageLink && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <p className="text-xs text-amber-800">
                        <span className="font-medium">
                          Template missing{" "}
                          <code>{"{{landing_page_link}}"}</code>
                        </span>{" "}
                        — the selected template does not contain this merge
                        field. The URL will be built but not injected.
                      </p>
                    </div>
                  </div>
                )}

                {/* Base URL */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Base URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={landingPageConfig.base_url}
                    onChange={(e) =>
                      setLandingPageConfig((prev) => ({
                        ...prev,
                        base_url: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    placeholder="https://ww2.uslawshield.com/landing-page/"
                  />
                </div>

                {/* Registration prefill — appends each recipient's
                    email/first/last/phone so an event landing page's form is
                    pre-populated for that invitee. */}
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={landingPagePrefill}
                    onChange={(e) => setLandingPagePrefill(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Prefill the registration form
                    <span className="block text-xs text-gray-500">
                      Appends each recipient's email, first/last name, and phone
                      to the link so an event landing page pre-fills their form.
                    </span>
                  </span>
                </label>

                {/* UTM fields — standard */}
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">
                    UTM Parameters
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "utm_source", label: "utm_source" },
                      { key: "utm_medium", label: "utm_medium" },
                      { key: "utm_campaign", label: "utm_campaign" },
                      { key: "utm_id", label: "utm_id" },
                      { key: "utm_term", label: "utm_term" },
                      { key: "utm_content", label: "utm_content" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-500 mb-1 font-mono">
                          {label}
                        </label>
                        <input
                          type="text"
                          value={
                            landingPageConfig[
                              key as keyof typeof landingPageConfig
                            ]
                          }
                          onChange={(e) =>
                            setLandingPageConfig((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                          placeholder={`e.g. email`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* USLS-specific tracking params */}
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">
                    Tracking Parameters
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: "affid", label: "affid" },
                      { key: "promo", label: "promo" },
                      { key: "ste", label: "ste" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-500 mb-1 font-mono">
                          {label}
                        </label>
                        <input
                          type="text"
                          value={
                            landingPageConfig[
                              key as keyof typeof landingPageConfig
                            ]
                          }
                          onChange={(e) =>
                            setLandingPageConfig((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                          placeholder={key === "ste" ? "{{state}}" : ""}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom query parameters */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600">
                      Custom query parameters
                    </p>
                    <button
                      type="button"
                      onClick={addCustomParam}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + Add parameter
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Define your own parameter names (e.g.{" "}
                    <span className="font-mono">ZID</span>) with a fixed value
                    or a <span className="font-mono">{"{{variable}}"}</span>.
                    Names are used exactly as typed (case preserved).
                  </p>
                  {customParams.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      No custom parameters yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {customParams.map((row, i) => (
                        <div
                          key={i}
                          className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center"
                        >
                          <input
                            type="text"
                            aria-label={`Custom parameter ${i + 1} name`}
                            value={row.name}
                            onChange={(e) =>
                              updateCustomParam(i, "name", e.target.value)
                            }
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                            placeholder="ZID"
                          />
                          <input
                            type="text"
                            aria-label={`Custom parameter ${i + 1} value`}
                            value={row.value}
                            onChange={(e) =>
                              updateCustomParam(i, "value", e.target.value)
                            }
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                            placeholder="{{zuora_account_id}}"
                          />
                          <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                            <input
                              type="checkbox"
                              aria-label={`Custom parameter ${i + 1} required`}
                              checked={Boolean(row.required)}
                              onChange={() => toggleCustomParamRequired(i)}
                            />
                            Required
                          </label>
                          <button
                            type="button"
                            aria-label={`Remove custom parameter ${i + 1}`}
                            onClick={() => removeCustomParam(i)}
                            className="px-2 py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Landing-config validation (inline, non-blocking for drafts) */}
                {landingValidationErrors.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-medium text-amber-800 mb-1">
                      Fix before scheduling:
                    </p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {landingValidationErrors.map((err, i) => (
                        <li key={i} className="text-xs text-amber-700">
                          {err}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Contact variable hints */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowVariableHints((v) => !v)}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <svg
                      className={`w-3.5 h-3.5 transition-transform ${showVariableHints ? "rotate-90" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    Available contact variables
                  </button>
                  {showVariableHints && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">
                        Type these into any parameter field to insert
                        per-contact values at send time:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {/* Exactly the variables the send-time landing-link resolver fills
                            (incl. household-aware effective_zuora_account_id), so every chip
                            offered here resolves. */}
                        {LANDING_LINK_CONTACT_VARS.map((v) => ({
                          value: v,
                        })).map((f) => (
                          <button
                            key={f.value}
                            type="button"
                            onClick={() =>
                              navigator.clipboard.writeText(`{{${f.value}}}`)
                            }
                            title={`Click to copy {{${f.value}}}`}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 cursor-pointer"
                          >
                            {`{{${f.value}}}`}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Click any variable to copy it to clipboard
                      </p>
                    </div>
                  )}
                </div>

                {/* Live URL preview */}
                {landingPagePreviewUrl && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">
                      URL Preview
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-mono text-gray-700 break-all">
                        {landingPagePreviewUrl}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {"{{variable}}"} placeholders resolve to contact values at
                      send time
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Email quality: per-flight risky opt-out */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="exclude_risky"
                checked={excludeRisky}
                onChange={(e) => setExcludeRisky(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <div className="flex-1">
                <label
                  htmlFor="exclude_risky"
                  className="text-sm font-medium text-gray-900"
                >
                  Exclude risky (unverified) addresses from this send
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Risky = NeverBounce could not verify the address (it may or
                  may not be deliverable). Off by default, so these are
                  included. Undeliverable (invalid) addresses are always
                  excluded, whether or not this is on. Turning this on trims
                  your audience — you'll see the exact valid / risky / invalid
                  breakdown on the send confirmation.
                </p>
              </div>
            </div>
          </div>
          {/* Audience Filters */}
          {(() => {
            const isLocked = !!(
              flight as { generate_vouchers?: boolean } | null
            )?.generate_vouchers;
            return (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="audience_filters_enabled"
                    checked={audienceFiltersEnabled}
                    onChange={(e) =>
                      setAudienceFiltersEnabled(e.target.checked)
                    }
                    disabled={isLocked}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="audience_filters_enabled"
                      className={`text-sm font-medium ${isLocked ? "text-gray-400" : "text-gray-900"}`}
                    >
                      Filter audience by contact attributes
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Only send to contacts matching specific attribute
                      conditions
                    </p>
                    {isLocked && (
                      <p className="text-xs text-amber-600 mt-1">
                        Filters are locked after vouchers are generated. To use
                        different filters, create a new flight.
                      </p>
                    )}
                  </div>
                </div>

                {audienceFiltersEnabled && (
                  <div className="mt-4 space-y-3">
                    {/* Shared suggestion list for tag scalar inputs (e.g.
                        does_not_have). Multi-tag inputs source from the same
                        availableTags via TagPillInput. */}
                    <datalist id="audience-filter-tag-suggestions">
                      {availableTags.map((t) => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                    {/* Match mode */}
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-medium text-gray-600">
                        Contacts must match
                      </span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="audience_match"
                          checked={audienceFiltersMatch === "all"}
                          onChange={() => setAudienceFiltersMatch("all")}
                          disabled={isLocked}
                          className="text-primary-600"
                        />
                        <span className="text-sm text-gray-700">
                          ALL conditions (AND)
                        </span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="audience_match"
                          checked={audienceFiltersMatch === "any"}
                          onChange={() => setAudienceFiltersMatch("any")}
                          disabled={isLocked}
                          className="text-primary-600"
                        />
                        <span className="text-sm text-gray-700">
                          ANY condition (OR)
                        </span>
                      </label>
                    </div>

                    {/* Condition rows */}
                    <div className="space-y-2">
                      {audienceFilterConditions.map((cond, idx) => {
                        const fieldDef = audienceFilterFields.find(
                          (f) => f.value === cond.field,
                        );
                        const fieldType = (fieldDef?.type ??
                          "text") as FilterFieldType;
                        const ops = OPERATORS_BY_TYPE[fieldType];
                        const opDef = ops.find(
                          (o) => o.value === cond.operator,
                        );
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            {/* Field selector */}
                            <select
                              value={cond.field}
                              disabled={isLocked}
                              onChange={(e) => {
                                const newField = e.target.value;
                                const newFieldDef = audienceFilterFields.find(
                                  (f) => f.value === newField,
                                );
                                const newType = (newFieldDef?.type ??
                                  "text") as FilterFieldType;
                                const newOps = OPERATORS_BY_TYPE[newType];
                                if (newType === "tag") {
                                  void ensureTagsLoaded();
                                }
                                if (newType === "list") {
                                  void ensureListsLoaded();
                                }
                                setAudienceFilterConditions((prev) =>
                                  prev.map((c, i) =>
                                    i === idx
                                      ? {
                                          field: newField,
                                          operator: newOps[0].value,
                                          value: "",
                                          values: [],
                                        }
                                      : c,
                                  ),
                                );
                              }}
                              className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                            >
                              {[
                                "Membership",
                                "Contact",
                                "Communication",
                                "Other",
                                "Custom Attributes",
                              ].map((group) => (
                                <optgroup key={group} label={group}>
                                  {audienceFilterFields
                                    .filter((f) => f.group === group)
                                    .map((f) => (
                                      <option key={f.value} value={f.value}>
                                        {f.label}
                                      </option>
                                    ))}
                                </optgroup>
                              ))}
                            </select>

                            {/* Operator selector */}
                            <select
                              value={cond.operator}
                              disabled={isLocked}
                              onChange={(e) =>
                                setAudienceFilterConditions((prev) =>
                                  prev.map((c, i) =>
                                    i === idx
                                      ? {
                                          ...c,
                                          operator: e.target.value,
                                          value: "",
                                          values: [],
                                        }
                                      : c,
                                  ),
                                )
                              }
                              className="w-44 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                            >
                              {ops.map((op) => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              ))}
                            </select>

                            {/* Value input — three shapes: scalar / multi / none.
                                Tag operators (whether scalar like does_not_have
                                or multi like has_any_of) ALL serialize to
                                cond.values to keep the storage convention
                                uniform with the edge evaluator. Tag scalar uses
                                a single-element values array; multi uses N. */}
                            {/* Zuora entitlement: decided by FIELD type, not operator valueShape.
                                Its operators are "multi", so nesting this inside the scalar branch
                                below meant it never rendered and the field fell through to a
                                free-text input. */}
                            {fieldType === "zuora" &&
                            opDef?.valueShape !== "none" ? (
                              // Multi-select over the live catalog. Values are exact strings —
                              // "Monthly Emergency Shield" and "Monthly Emergency Shield -
                              // Secondary" are different plans and never match each other.
                              <div className="flex flex-col gap-1">
                                <input
                                  onKeyDown={preventEnterSubmit}
                                  type="text"
                                  value={zuoraFilters[idx] ?? ""}
                                  onChange={(e) =>
                                    setZuoraFilters((prev) => ({
                                      ...prev,
                                      [idx]: e.target.value,
                                    }))
                                  }
                                  placeholder="Type to filter, e.g. defend"
                                  className="w-80 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
                                />
                                <select
                                  multiple
                                  size={7}
                                  value={cond.values ?? []}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    setAudienceFilterConditions((prev) =>
                                      prev.map((c, i) =>
                                        i === idx
                                          ? {
                                              ...c,
                                              value: "",
                                              values: Array.from(
                                                e.target.selectedOptions,
                                              ).map((o) => o.value),
                                            }
                                          : c,
                                      ),
                                    )
                                  }
                                  className="w-80 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                                >
                                  {(
                                    zuoraValues[
                                      ZUORA_FIELD_KIND[cond.field] ?? "plan"
                                    ] ?? []
                                  )
                                    .filter((o) => {
                                      const n = (zuoraFilters[idx] ?? "")
                                        .trim()
                                        .toLowerCase();
                                      // Never hide an already-selected value, or typing a filter
                                      // would silently drop it from the condition.
                                      return (
                                        !n ||
                                        o.value.toLowerCase().includes(n) ||
                                        (cond.values ?? []).includes(o.value)
                                      );
                                    })
                                    .map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.value} (
                                        {o.contact_count.toLocaleString()})
                                      </option>
                                    ))}
                                </select>
                                <span className="text-xs text-gray-500">
                                  {zuoraValuesError
                                    ? `Could not load values: ${zuoraValuesError}`
                                    : "Hold ⌘/Ctrl to select several. Values are A–Z; counts are contacts with a current active subscription."}
                                </span>
                                {(cond.values ?? []).length > 0 && (
                                  <span className="text-xs text-gray-600">
                                    Selected: {(cond.values ?? []).join(", ")}
                                  </span>
                                )}
                              </div>
                            ) : opDef?.valueShape === "scalar" ? (
                              fieldType === "tag" ? (
                                // Tag scalar input (does_not_have): free-text
                                // with datalist suggestions — the operator can
                                // type any tag, picker surfaces known ones.
                                // Stored as values[0], not value.
                                <input
                                  type="text"
                                  list="audience-filter-tag-suggestions"
                                  value={cond.values?.[0] ?? ""}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    setAudienceFilterConditions((prev) =>
                                      prev.map((c, i) =>
                                        i === idx
                                          ? {
                                              ...c,
                                              value: "",
                                              values: e.target.value
                                                ? [e.target.value]
                                                : [],
                                            }
                                          : c,
                                      ),
                                    )
                                  }
                                  className="w-36 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                                  placeholder="tag"
                                />
                              ) : fieldType === "email_quality" ? (
                                // Email-quality bucket picker — value is one of
                                // the 4 buckets.
                                <select
                                  value={cond.value}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    setAudienceFilterConditions((prev) =>
                                      prev.map((c, i) =>
                                        i === idx
                                          ? { ...c, value: e.target.value }
                                          : c,
                                      ),
                                    )
                                  }
                                  className="w-56 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                                >
                                  <option value="">Select quality…</option>
                                  {EMAIL_QUALITY_FIELD_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              ) : fieldType === "list" ? (
                                // List picker — value is a list_id.
                                <select
                                  value={cond.value}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    setAudienceFilterConditions((prev) =>
                                      prev.map((c, i) =>
                                        i === idx
                                          ? { ...c, value: e.target.value }
                                          : c,
                                      ),
                                    )
                                  }
                                  className="w-44 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                                >
                                  <option value="">Select a list…</option>
                                  {availableLists.map((l) => (
                                    <option key={l.id} value={l.id}>
                                      {l.name}
                                    </option>
                                  ))}
                                  {/* Preserve a saved id not in the fetched list. */}
                                  {cond.value &&
                                    !availableLists.some(
                                      (l) => l.id === cond.value,
                                    ) && (
                                      <option value={cond.value}>
                                        {cond.value}
                                      </option>
                                    )}
                                </select>
                              ) : (
                                // Date fields render a date picker for absolute
                                // operators (is on/before/after) and a numeric
                                // "days" input for the relative days_ago window.
                                <input
                                  type={
                                    fieldType === "date"
                                      ? cond.operator === "days_ago"
                                        ? "number"
                                        : "date"
                                      : "text"
                                  }
                                  min={
                                    fieldType === "date" &&
                                    cond.operator === "days_ago"
                                      ? 0
                                      : undefined
                                  }
                                  value={cond.value}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    setAudienceFilterConditions((prev) =>
                                      prev.map((c, i) =>
                                        i === idx
                                          ? { ...c, value: e.target.value }
                                          : c,
                                      ),
                                    )
                                  }
                                  className="w-36 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                                  placeholder={
                                    fieldType === "date"
                                      ? cond.operator === "days_ago"
                                        ? "days"
                                        : "YYYY-MM-DD"
                                      : "value"
                                  }
                                />
                              )
                            ) : opDef?.valueShape === "multi" ? (
                              <TagPillInput
                                values={cond.values ?? []}
                                disabled={isLocked}
                                suggestions={availableTags}
                                onChange={(next) =>
                                  setAudienceFilterConditions((prev) =>
                                    prev.map((c, i) =>
                                      i === idx ? { ...c, values: next } : c,
                                    ),
                                  )
                                }
                              />
                            ) : (
                              <div className="w-36" />
                            )}

                            {/* Remove button */}
                            {!isLocked && (
                              <button
                                type="button"
                                onClick={() =>
                                  setAudienceFilterConditions((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  )
                                }
                                className="p-1 text-gray-400 hover:text-red-500"
                                title="Remove condition"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Add condition */}
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() =>
                          setAudienceFilterConditions((prev) => [
                            ...prev,
                            {
                              field: "member_status",
                              operator: "is_empty",
                              value: "",
                            },
                          ])
                        }
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Add condition
                      </button>
                    )}

                    {/* Validation error */}
                    {errors.audience_filters && (
                      <p className="text-sm text-red-600">
                        {errors.audience_filters}
                      </p>
                    )}

                    {/* Summary sentence */}
                    {audienceFilterSummary && (
                      <p className="text-xs text-gray-500 italic">
                        {audienceFilterSummary}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* SMS Configuration — only when SMS channel selected */}
      {channel === "sms" && (
        <div>
          <label
            htmlFor="sms_template_id"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            SMS Template <span className="text-red-500">*</span>
          </label>
          <select
            id="sms_template_id"
            value={formData.sms_template_id}
            onChange={(e) => handleChange("sms_template_id", e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.sms_template_id ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select a template...</option>
            {smsTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          {errors.sms_template_id && (
            <p className="mt-1 text-sm text-red-600">
              {errors.sms_template_id}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Select an SMS template to send via Sinch
          </p>
        </div>
      )}

      {/* Voucher generation — hidden for reminders (they reuse the source's codes) */}
      {isReminder && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 text-sm text-gray-600">
          This reminder reuses the source flight's voucher codes — no new
          vouchers are generated.
        </div>
      )}
      {!isReminder && (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="generate_vouchers"
              checked={generateVouchers}
              onChange={(e) => setGenerateVouchers(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div className="flex-1">
              <label
                htmlFor="generate_vouchers"
                className="text-sm font-medium text-gray-900"
              >
                Generate vouchers for recipients
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Each contact gets a unique voucher whose value and name come
                from the offer you pick below.
              </p>
            </div>
          </div>

          {generateVouchers && (
            <div className="mt-3 space-y-3">
              {/* Required offer selector — co-located with the toggle (no scroll).
                Lists ALL active offers; the voucher derives from the chosen one. */}
              <div>
                <label
                  htmlFor="offer_id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Associated Offer <span className="text-red-500">*</span>
                </label>
                <select
                  id="offer_id"
                  value={formData.offer_id}
                  onChange={(e) => handleChange("offer_id", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.generate_vouchers
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select an offer…</option>
                  {availableOffers.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                      {o.value_amount ? ` ($${o.value_amount})` : ""}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  The voucher's value and name come from this offer. Don't see
                  it? Create it under <strong>Offers</strong> (active, with a
                  value), then reopen this form.
                </p>
                {errors.generate_vouchers && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.generate_vouchers}
                  </p>
                )}
              </div>
              {/* The other required step lives in the Email Template section above. */}
              <p className="text-sm text-gray-600">
                <strong>Don't forget</strong> to map your template's voucher
                variables (<code>{"{{voucher_code}}"}</code>,{" "}
                <code>{"{{qr_image_url}}"}</code>) to the{" "}
                <strong>Voucher</strong> source in the Email Template
                field-mapping above.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Optional: Scheduled Send */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            htmlFor="scheduled_send_at"
            className="block text-sm font-medium text-gray-700"
          >
            Schedule Send (Optional)
          </label>
          {/* Schedule-clearing affordances — two states:
              1. Flight is already in "scheduled" status (DB-level): show
                 "Cancel Schedule" which delegates to the parent's
                 confirmation + updateFlight flow (single source of
                 truth on FlightDetail).
              2. Flight is still draft but the operator has put a date
                 in scheduled_send_at: show "Clear" which just empties
                 the local form field — no DB action needed since the
                 operator hasn't moved the flight to "scheduled" status
                 yet via the detail-page Schedule button. Surfaced as a
                 real gap by an operator 2026-05-14 — the original gate
                 (status === 'scheduled' only) missed this case. */}
          {flight?.status === "scheduled" && onRequestCancelSchedule ? (
            <button
              type="button"
              onClick={onRequestCancelSchedule}
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              Cancel Schedule
            </button>
          ) : formData.scheduled_send_at ? (
            <button
              type="button"
              onClick={() => handleChange("scheduled_send_at", "")}
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              Clear
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="datetime-local"
            id="scheduled_send_at"
            value={formData.scheduled_send_at}
            onChange={(e) => handleChange("scheduled_send_at", e.target.value)}
            min={(() => {
              const now = new Date();
              return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
            })()}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.scheduled_send_at ? "border-red-500" : "border-gray-300"
            }`}
          />
          <TimezoneSelector
            value={sendTimezone}
            onChange={setSendTimezone}
            label=""
          />
        </div>
        {errors.scheduled_send_at && (
          <p className="mt-1 text-sm text-red-600">
            {errors.scheduled_send_at}
          </p>
        )}
        {errors.landing_page_config && (
          <p className="mt-1 text-sm text-red-600">
            {errors.landing_page_config}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          {formData.scheduled_send_at
            ? "When saved, this flight will auto-send at the time above. To send manually, cancel the schedule first."
            : "Leave empty to save as draft. You can send manually from the campaign page."}
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              What happens when you create this flight:
            </p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              {generateVouchers && (
                <li>
                  A unique voucher code will be generated for each contact in
                  the selected segment
                </li>
              )}
              <li>
                The flight will be in "draft" status until you schedule it
              </li>
              {channel === "email" && (
                <li>
                  Emails will be sent via SendGrid with personalized merge
                  fields
                </li>
              )}
              {channel === "sms" && (
                <li>SMS messages will be sent via Sinch</li>
              )}
              <li>Delivery status will update automatically via webhooks</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tag Actions */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-900">Tag Actions</h3>
        <p className="text-xs text-gray-500 -mt-2">
          Apply or remove a tag on each contact when this flight is sent.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <TagAutocompleteInput
            id="flight-apply-tag"
            label="Apply tag on send"
            value={applyTag}
            onChange={setApplyTag}
            placeholder="e.g. received_email"
            helpText="Added to each contact who receives this email"
          />
          <TagAutocompleteInput
            id="flight-remove-tag"
            label="Remove tag on send"
            value={removeTag}
            onChange={setRemoveTag}
            placeholder="e.g. pending_outreach"
            helpText="Removed from each contact who receives this email"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          {flight ? "Update Flight" : "Create Flight"}
        </button>
      </div>

      {/* SendGrid Template Modal — picker / changer */}
      <SendGridTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelect={handleTemplateSelect}
        selectedTemplateId={formData.email_template_id}
      />

      {/* Inline preview of the currently-selected template, with the
          operator's chosen field mappings substituted. Reuses the
          html_content / preview already on the loaded template details
          object — no extra fetch on click. */}
      {selectedEmailTemplate && (
        <TemplatePreviewModal
          isOpen={showTemplatePreview}
          onClose={() => setShowTemplatePreview(false)}
          templateName={selectedEmailTemplate.name}
          templateHtml={
            selectedEmailTemplate.html_content ||
            selectedEmailTemplate.preview ||
            ""
          }
          fieldMappings={fieldMappings}
          hasEmailCategory={!!formData.subscription_group_id}
          flightSubject={formData.email_subject ?? ""}
          flightPreheader={preheader}
        />
      )}
    </form>
  );
};

export default FlightForm;
